import * as THREE from 'three';
import { Capsule } from 'three/addons/math/Capsule.js';
import { operatorFor, paletteFor, ALL_OPERATORS } from '../gameplay/operators.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { applySkill, DEFAULT_DIFFICULTY, difficultyById } from './Difficulty.js';

/* =============================================================== scratch === */

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _sq = new THREE.Vector3();   // squadTick scratch; do NOT alias _v3
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _m = new THREE.Matrix4();
const _up = new THREE.Vector3(0, 1, 0);
const _sph = new THREE.Sphere();
// Scratch for the bone-driven hit volumes (see Enemy.raycastHit).
const _hitA = new THREE.Vector3();
const _hitB = new THREE.Vector3();
const _hitC = new THREE.Vector3();
const _hitD = new THREE.Vector3();
const _hitP0 = new THREE.Vector3();
const _hitP1 = new THREE.Vector3();

const clamp = THREE.MathUtils.clamp;
const lerp = THREE.MathUtils.lerp;
const damp = THREE.MathUtils.damp;
function wrapPi(a) { while (a > Math.PI) a -= Math.PI * 2; while (a < -Math.PI) a += Math.PI * 2; return a; }

/* ============================================================== textures === */

const _texCache = new Map();

function canvasTex(key, w, h, draw, { repeat = 1, srgb = true } = {}) {
  if (_texCache.has(key)) return _texCache.get(key);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  draw(c.getContext('2d'), w, h);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.anisotropy = 8;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  _texCache.set(key, t);
  return t;
}

function noiseOverlay(ctx, w, h, amount, alpha) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * amount;
    d[i] = clamp(d[i] + n, 0, 255);
    d[i + 1] = clamp(d[i + 1] + n, 0, 255);
    d[i + 2] = clamp(d[i + 2] + n, 0, 255);
  }
  ctx.putImageData(img, 0, 0);
  if (alpha) {
    ctx.globalAlpha = alpha;
    ctx.globalAlpha = 1;
  }
}

/** Multicam-ish blob camouflage. */
function camoTex(key, cols) {
  return canvasTex(`camo:${key}`, 256, 256, (ctx, w, h) => {
    ctx.fillStyle = cols[0];
    ctx.fillRect(0, 0, w, h);
    for (let layer = 1; layer < cols.length; layer++) {
      ctx.fillStyle = cols[layer];
      const blobs = 26 - layer * 4;
      for (let i = 0; i < blobs; i++) {
        const cx = Math.random() * w, cy = Math.random() * h;
        const rr = 12 + Math.random() * (34 - layer * 5);
        ctx.beginPath();
        const pts = 7 + ((Math.random() * 4) | 0);
        for (let p = 0; p <= pts; p++) {
          const a = (p / pts) * Math.PI * 2;
          const r = rr * (0.55 + Math.random() * 0.75);
          const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r * 0.8;
          if (p === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
        // wrap copies so the pattern tiles without a visible seam
        for (const [dx, dy] of [[w, 0], [-w, 0], [0, h], [0, -h]]) {
          ctx.save(); ctx.translate(dx, dy); ctx.fill(); ctx.restore();
        }
      }
    }
    noiseOverlay(ctx, w, h, 26);
  }, { repeat: 1 });
}

/** Cordura / nylon weave for pouches and the plate carrier. */
function nylonTex(key, base) {
  return canvasTex(`nylon:${key}`, 128, 128, (ctx, w, h) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);
    // The weave lines used to sit at 0.22 alpha every 4 px, which cost the
    // material ~11% of its albedo once mipped — the plate carrier read as a
    // black slab in anything but full sun.
    ctx.strokeStyle = 'rgba(0,0,0,0.13)';
    ctx.lineWidth = 1;
    for (let i = 0; i < w; i += 4) {
      ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, h); ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.09)';
    for (let i = 2; i < h; i += 4) {
      ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(w, i); ctx.stroke();
    }
    noiseOverlay(ctx, w, h, 22);
  }, { repeat: 1 });
}

/**
 * Rubber / moulded polymer for boots, gloves, optics and the weapon furniture.
 * Deliberately mid-grey rather than black: a pure-black albedo makes every one
 * of these parts collapse into the same silhouette blob in shadow.
 */
function rubberTex(key, base) {
  return canvasTex(`rub:${key}`, 128, 128, (ctx, w, h) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);
    // Moulded pebble grain.
    for (let i = 0; i < 900; i++) {
      const g = Math.random() < 0.5 ? 255 : 0;
      ctx.fillStyle = `rgba(${g},${g},${g},0.05)`;
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h, 0.8 + Math.random() * 2.2, 0, 7);
      ctx.fill();
    }
    // Faint wear scuffs so the flat panels catch a value break.
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    for (let i = 0; i < 40; i++) {
      ctx.lineWidth = 0.5 + Math.random();
      ctx.beginPath();
      const x = Math.random() * w, y = Math.random() * h;
      ctx.moveTo(x, y);
      ctx.lineTo(x + (Math.random() - 0.5) * 30, y + (Math.random() - 0.5) * 30);
      ctx.stroke();
    }
    noiseOverlay(ctx, w, h, 16);
  }, { repeat: 1 });
}

function skinTex(key, base) {
  return canvasTex(`skin:${key}`, 128, 128, (ctx, w, h) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 400; i++) {
      ctx.fillStyle = `rgba(${60 + Math.random() * 40 | 0},${30 + Math.random() * 30 | 0},20,0.06)`;
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h, 1 + Math.random() * 3, 0, 7);
      ctx.fill();
    }
    noiseOverlay(ctx, w, h, 14);
  }, { repeat: 1 });
}

/* =============================================================== geometry == */

/** Beveled box: SDF-rounds a segmented box so every hard edge catches light. */
function rbox(w, h, d, r = 0.02, seg = 3) {
  r = Math.min(r, w / 2 - 1e-4, h / 2 - 1e-4, d / 2 - 1e-4);
  const g = new THREE.BoxGeometry(w, h, d, seg, seg, seg);
  const pos = g.attributes.position;
  const hx = w / 2 - r, hy = h / 2 - r, hz = d / 2 - r;
  const p = new THREE.Vector3(), q = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    p.fromBufferAttribute(pos, i);
    q.set(clamp(p.x, -hx, hx), clamp(p.y, -hy, hy), clamp(p.z, -hz, hz));
    p.sub(q);
    const l = p.length();
    if (l > 1e-6) p.multiplyScalar(r / l);
    pos.setXYZ(i, q.x + p.x, q.y + p.y, q.z + p.z);
  }
  g.computeVertexNormals();
  return g;
}

/**
 * A lofted sweep: the primitive the body is actually made of.
 *
 * ## Why this exists
 *
 * Everything here used to be a bevelled box, a constant-radius capsule or a
 * sphere, and that vocabulary cannot express a body. A torso became a slab, a
 * bicep became a tube, a shoulder became a ball bolted to the ribs, and the
 * silhouette came out of straight lines and circular arcs — which is precisely
 * what reads as blocky however many triangles it is subdivided into. The model
 * was already 19 000 triangles when it looked like that, so subdividing was
 * never the answer.
 *
 * A loft sweeps a closed cross-section along a path. Each section carries its
 * own centre, its own two radii and its own ROUNDNESS, so one function covers
 * the whole model:
 *
 *   - flesh, at roundness 2 (a true ellipse): arms, legs, neck, cranium;
 *   - gear, at roundness 4-8 (a superellipse, approaching a rounded
 *     rectangle): a plate carrier that wraps the ribs, a boot last.
 *
 * Between two sections the radius, the centre and the roundness all interpolate
 * with a smoothstep, so a bicep swells and a waist pinches without a crease at
 * the ring that defines it.
 *
 * @param {Array<{y:number, rx:number, rz:number, x?:number, z?:number,
 *   round?:number, rot?:number}>} sections  bottom to top. `rot` twists the
 *   cross-section about Y, which is how a deltoid faces outward.
 * @param {{radial?:number, rings?:number, capTop?:boolean, capBottom?:boolean}} opts
 *   `rings` is the number of subdivisions BETWEEN adjacent sections; the
 *   interpolation is what makes the surface read as one mass rather than a
 *   stack of hoops.
 */
function loft(sections, { radial = 20, rings = 4, capTop = true, capBottom = true } = {}) {
  const S = sections;
  if (S.length < 2) throw new Error('loft needs at least two sections');

  // Resample: every span between two authored sections becomes `rings` rows,
  // smoothstepped so the tangent is continuous across the authored ring.
  const rows = [];
  for (let i = 0; i < S.length - 1; i++) {
    const a = S[i], b = S[i + 1];
    const last = i === S.length - 2;
    const steps = last ? rings + 1 : rings;
    for (let k = 0; k < steps; k++) {
      const u = k / rings;
      const t = u * u * (3 - 2 * u);            // smoothstep
      rows.push({
        y: lerp(a.y, b.y, t),
        x: lerp(a.x ?? 0, b.x ?? 0, t),
        z: lerp(a.z ?? 0, b.z ?? 0, t),
        rx: lerp(a.rx, b.rx, t),
        rz: lerp(a.rz, b.rz, t),
        round: lerp(a.round ?? 2, b.round ?? 2, t),
        rot: lerp(a.rot ?? 0, b.rot ?? 0, t),
      });
    }
  }

  const positions = [];
  const indices = [];
  const uvs = [];

  const ringVert = (r, j) => {
    const th = (j / radial) * Math.PI * 2;
    const c = Math.cos(th), sn = Math.sin(th);
    /*
     * Superellipse via a signed power. |cos|^(2/n) keeps the corner sharp as
     * n grows while the sign keeps the quadrant — the textbook form, and the
     * reason one exponent spans an ellipse (n=2) to a rounded box (n=8)
     * without a second primitive.
     */
    const e = 2 / (r.round || 2);
    const px = Math.sign(c) * Math.pow(Math.abs(c), e) * r.rx;
    const pz = Math.sign(sn) * Math.pow(Math.abs(sn), e) * r.rz;
    const cr = Math.cos(r.rot), sr = Math.sin(r.rot);
    return [r.x + px * cr - pz * sr, r.y, r.z + px * sr + pz * cr];
  };

  for (let i = 0; i < rows.length; i++) {
    for (let j = 0; j <= radial; j++) {
      // The seam column is duplicated so the UV can run 0..1 without wrapping
      // a triangle the whole way back round the mass.
      const v = ringVert(rows[i], j % radial);
      positions.push(v[0], v[1], v[2]);
      uvs.push(j / radial, i / (rows.length - 1));
    }
  }
  const stride = radial + 1;
  for (let i = 0; i < rows.length - 1; i++) {
    for (let j = 0; j < radial; j++) {
      const a = i * stride + j, b = a + 1, c = a + stride, d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  // Caps: a fan to a centre vertex at the end ring. Flat, but they are the two
  // places a loft is always buried inside another mass or a joint.
  const cap = (rowIndex, up) => {
    const r = rows[rowIndex];
    const centre = positions.length / 3;
    positions.push(r.x, r.y, r.z);
    uvs.push(0.5, up ? 1 : 0);
    const base = rowIndex * stride;
    for (let j = 0; j < radial; j++) {
      const a = base + j, b = base + j + 1;
      if (up) indices.push(a, b, centre); else indices.push(b, a, centre);
    }
  };
  if (capBottom) cap(0, false);
  if (capTop) cap(rows.length - 1, true);

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(indices);
  g.computeVertexNormals();

  /*
   * Weld the seam normals.
   *
   * Column 0 and column `radial` are the SAME point in space, duplicated so the
   * UV can run 0..1 without a triangle wrapping the whole way back round the
   * mass. `computeVertexNormals` does not know that, so each gets only the
   * faces on its own side and the two disagree — which draws a hard crease
   * straight down every limb. Averaging the pair and writing it back to both is
   * the whole fix, and it has to happen after the normals are computed and
   * before anything else touches the geometry.
   */
  const nrm = g.attributes.normal;
  for (let i = 0; i < rows.length; i++) {
    const a = i * stride, b = a + radial;
    const nx = nrm.getX(a) + nrm.getX(b);
    const ny = nrm.getY(a) + nrm.getY(b);
    const nz = nrm.getZ(a) + nrm.getZ(b);
    const l = Math.hypot(nx, ny, nz) || 1;
    nrm.setXYZ(a, nx / l, ny / l, nz / l);
    nrm.setXYZ(b, nx / l, ny / l, nz / l);
  }
  nrm.needsUpdate = true;
  return g;
}

/**
 * Push a mesh's vertices around with smooth anatomical blobs.
 *
 * ## Why a face cannot be assembled from parts
 *
 * The body stopped being blocky the moment its masses became lofts, and the
 * face did not, because the face was never a mass — it was a brow slab, an eye
 * bar, a nose box and a chin block stuck on the front of a skull. Applied parts
 * cannot look like a face at any polygon count: a real brow is the SAME surface
 * as the forehead, swelling and receding, and the moment it is a separate box
 * it has its own silhouette and its own hard edge against the skin.
 *
 * So features are displacement instead. Each one is an ellipsoidal field with a
 * smooth falloff; inside it, vertices move along a direction, and where two
 * fields overlap their contributions add. A brow ridge and the eye socket under
 * it are one continuous surface because they are two pushes on the same
 * vertices, not two objects.
 *
 * The falloff is `(1 - t^2)^2`, which reaches zero with zero gradient at the
 * field boundary — so a feature fades into the surrounding skin instead of
 * ending on a crease, which is the whole point.
 *
 * @param {THREE.BufferGeometry} geo  modified in place
 * @param {Array<{x?:number, y:number, z?:number, rx:number, ry:number,
 *   rz:number, amount:number, dir?:[number,number,number], mirror?:boolean}>} features
 *   `dir` omitted means "along the vertex normal". `mirror` applies the feature
 *   at both +x and -x, which is how a face is built.
 */
/**
 * Resample a loft profile over a y range, offset outwards.
 *
 * Used to build a garment from the body it is worn on: a mask from the skull,
 * so the cloth follows the jaw and the cheekbones instead of being a shape
 * somebody kept in sync with them by hand.
 *
 * @param {Array} sections  the source profile, bottom to top
 * @param {number} yLo @param {number} yHi  the band to take
 * @param {number} count  rows across the band
 * @param {number} offset  metres pushed outwards
 * @param {number} flatten  added to the roundness — cloth over a face reads
 *   slightly squarer than the bone under it
 */
function profileBand(sections, yLo, yHi, count, offset, flatten = 0) {
  const at_ = (y) => {
    // Below or above the source profile, hold the end section rather than
    // extrapolating: a mask that reaches under the chin should follow the chin,
    // not a line shooting off into space.
    if (y <= sections[0].y) return sections[0];
    const last = sections[sections.length - 1];
    if (y >= last.y) return last;
    for (let i = 0; i < sections.length - 1; i++) {
      const a = sections[i], b = sections[i + 1];
      if (y < a.y || y > b.y) continue;
      const t = (y - a.y) / (b.y - a.y);
      return {
        rx: lerp(a.rx, b.rx, t), rz: lerp(a.rz, b.rz, t),
        z: lerp(a.z ?? 0, b.z ?? 0, t), x: lerp(a.x ?? 0, b.x ?? 0, t),
        round: lerp(a.round ?? 2, b.round ?? 2, t),
      };
    }
    return last;
  };
  const out = [];
  for (let i = 0; i < count; i++) {
    const y = lerp(yLo, yHi, i / (count - 1));
    const p = at_(y);
    out.push({
      y, x: p.x ?? 0, z: p.z ?? 0,
      rx: p.rx + offset, rz: p.rz + offset,
      round: (p.round ?? 2) + flatten,
    });
  }
  return out;
}

function sculpt(geo, features) {
  const pos = geo.attributes.position;
  const nrm = geo.attributes.normal;
  // Read the ORIGINAL normals once. Displacing along normals that are being
  // updated as you go makes the result depend on vertex order.
  const n0 = nrm.array.slice();

  const expanded = [];
  for (const f of features) {
    if (f.mirror) {
      expanded.push({ ...f, x: Math.abs(f.x ?? 0) });
      expanded.push({ ...f, x: -Math.abs(f.x ?? 0), dir: f.dir ? [-f.dir[0], f.dir[1], f.dir[2]] : undefined });
    } else expanded.push(f);
  }

  for (let i = 0; i < pos.count; i++) {
    const px = pos.getX(i), py = pos.getY(i), pz = pos.getZ(i);
    let dx = 0, dy = 0, dz = 0;
    for (const f of expanded) {
      const ux = (px - (f.x ?? 0)) / f.rx;
      const uy = (py - f.y) / f.ry;
      const uz = (pz - (f.z ?? 0)) / f.rz;
      const t2 = ux * ux + uy * uy + uz * uz;
      if (t2 >= 1) continue;
      const w = (1 - t2) * (1 - t2);
      const a = f.amount * w;
      if (f.dir) { dx += f.dir[0] * a; dy += f.dir[1] * a; dz += f.dir[2] * a; }
      else { dx += n0[i * 3] * a; dy += n0[i * 3 + 1] * a; dz += n0[i * 3 + 2] * a; }
    }
    if (dx || dy || dz) pos.setXYZ(i, px + dx, py + dy, pz + dz);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/**
 * Re-weld a loft's seam normals after the surface has been moved.
 *
 * `loft` welds them once, but `sculpt` recomputes normals from scratch and the
 * seam splits again — a hard line down the centre of the face, which is the one
 * place on the model nobody would miss it.
 */
function weldSeam(geo, radial, rows) {
  const nrm = geo.attributes.normal;
  const stride = radial + 1;
  for (let i = 0; i < rows; i++) {
    const a = i * stride, b = a + radial;
    const nx = nrm.getX(a) + nrm.getX(b);
    const ny = nrm.getY(a) + nrm.getY(b);
    const nz = nrm.getZ(a) + nrm.getZ(b);
    const l = Math.hypot(nx, ny, nz) || 1;
    nrm.setXYZ(a, nx / l, ny / l, nz / l);
    nrm.setXYZ(b, nx / l, ny / l, nz / l);
  }
  nrm.needsUpdate = true;
  return geo;
}

function caps(r, len, rs = 5, cs = 10) { return new THREE.CapsuleGeometry(r, len, rs, cs); }
function sph(r, ws = 12, hs = 10) { return new THREE.SphereGeometry(r, ws, hs); }
/**
 * A spherical cap, measured in polar angle from the +Y pole.
 *
 * Exists for eyelids. A flattened ellipsoid parked over an eyeball tapers to a
 * knife edge at its rim, and right at that rim the eyeball — which is rounder —
 * pokes out IN FRONT of it, so the lid stops occluding exactly where it matters
 * and the eye reads as a bare ball. A cap concentric with the eyeball and a
 * fraction larger occludes cleanly everywhere it exists.
 */
function sphCap(r, thetaStart, thetaLength, ws = 18) {
  return new THREE.SphereGeometry(r, ws, 10, 0, Math.PI * 2, thetaStart, thetaLength);
}
function cyl(rt, rb, h, s = 10) { return new THREE.CylinderGeometry(rt, rb, h, s); }

/* ================================================================== rig ==== */

/**
 * Bone table. Each entry: [name, parent, offsetFromParent].
 * Offsets are the rest pose of a 1.80 m soldier standing on y=0.
 */
const BONES = [
  ['root', null, [0, 0, 0]],
  ['hips', 'root', [0, 0.98, 0]],
  ['spine', 'hips', [0, 0.12, 0]],
  ['chest', 'spine', [0, 0.17, 0]],
  ['neck', 'chest', [0, 0.19, 0.006]],
  ['head', 'neck', [0, 0.085, 0]],
  ['clavL', 'chest', [0.055, 0.125, 0]],
  ['shL', 'clavL', [0.135, 0.008, 0]],
  ['elL', 'shL', [0, -0.27, 0]],
  ['wrL', 'elL', [0, -0.25, 0]],
  ['hnL', 'wrL', [0, -0.07, 0]],
  ['clavR', 'chest', [-0.055, 0.125, 0]],
  ['shR', 'clavR', [-0.135, 0.008, 0]],
  ['elR', 'shR', [0, -0.27, 0]],
  ['wrR', 'elR', [0, -0.25, 0]],
  ['hnR', 'wrR', [0, -0.07, 0]],
  ['hipL', 'hips', [0.105, -0.04, 0]],
  ['knL', 'hipL', [0, -0.44, 0]],
  ['anL', 'knL', [0, -0.42, 0]],
  ['toL', 'anL', [0, -0.055, 0.13]],
  ['hipR', 'hips', [-0.105, -0.04, 0]],
  ['knR', 'hipR', [0, -0.44, 0]],
  ['anR', 'knR', [0, -0.42, 0]],
  ['toR', 'anR', [0, -0.055, 0.13]],
];

function buildSkeletonBones() {
  const map = {};
  const list = [];
  for (const [name, parent, off] of BONES) {
    const b = new THREE.Bone();
    b.name = name;
    b.position.set(off[0], off[1], off[2]);
    if (parent) map[parent].add(b);
    map[name] = b;
    list.push(b);
  }
  map.root.updateMatrixWorld(true);
  return { map, list };
}

/** Rest-pose world positions + per-bone rest segment used for auto-skinning. */
function restData() {
  const { map, list } = buildSkeletonBones();
  const world = {};
  for (const b of list) world[b.name] = new THREE.Vector3().setFromMatrixPosition(b.matrixWorld);
  const index = {};
  list.forEach((b, i) => { index[b.name] = i; });
  // Segment for each bone: from the bone to its first child (or a short stub).
  const seg = {};
  for (const [name, parent] of BONES) {
    const child = BONES.find((e) => e[1] === name);
    const a = world[name];
    const b = child ? world[child[0]] : a.clone().add(new THREE.Vector3(0, 0.06, 0));
    seg[name] = { a, b, i: index[name] };
    void parent;
  }
  return { world, index, seg, boneOrder: list.map((b) => b.name) };
}

const REST = restData();

function distToSeg(p, a, b) {
  _v.subVectors(b, a);
  const l2 = _v.lengthSq();
  let t = l2 > 1e-9 ? _v2.subVectors(p, a).dot(_v) / l2 : 0;
  t = clamp(t, 0, 1);
  _v3.copy(a).addScaledVector(_v, t);
  return _v3.distanceTo(p);
}

/**
 * Auto-skin a piece of geometry authored in model space against a whitelist of
 * bones. Inverse-cube distance falloff gives smoothly closing joints — no
 * segment gaps, which was the biggest tell of the old mannequin rig.
 */
function skinPiece(geo, boneNames, { rigid = false, power = 3.2 } = {}) {
  const pos = geo.attributes.position;
  const n = pos.count;
  const si = new Uint16Array(n * 4);
  const sw = new Float32Array(n * 4);
  const segs = boneNames.map((nm) => REST.seg[nm]);
  const p = new THREE.Vector3();
  const wbuf = new Float64Array(segs.length);

  for (let i = 0; i < n; i++) {
    if (rigid || segs.length === 1) {
      si[i * 4] = segs[0].i; sw[i * 4] = 1;
      continue;
    }
    p.fromBufferAttribute(pos, i);
    let max = 0;
    for (let s = 0; s < segs.length; s++) {
      const d = distToSeg(p, segs[s].a, segs[s].b);
      const w = 1 / Math.pow(d + 0.018, power);
      wbuf[s] = w;
      if (w > max) max = w;
    }
    // Keep the four strongest influences above 6% of the peak.
    const picked = [];
    for (let s = 0; s < segs.length; s++) if (wbuf[s] > max * 0.06) picked.push(s);
    picked.sort((a, b) => wbuf[b] - wbuf[a]);
    let total = 0;
    const k = Math.min(4, picked.length);
    for (let j = 0; j < k; j++) total += wbuf[picked[j]];
    for (let j = 0; j < k; j++) {
      si[i * 4 + j] = segs[picked[j]].i;
      sw[i * 4 + j] = wbuf[picked[j]] / total;
    }
  }
  geo.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(si, 4));
  geo.setAttribute('skinWeight', new THREE.Float32BufferAttribute(sw, 4));
  if (geo.index) geo.deleteAttribute('uv2');
  return geo;
}

/* =============================================================== loadout === */

/**
 * Albedo is authored physically: nothing sits below ~0.045 linear (sRGB #3a),
 * because real "black" nylon and painted composite still bounce 4-6% of the
 * light that lands on them.
 *
 * The palettes themselves moved to src/gameplay/operators.js, because they are
 * no longer an anonymous pool a bot draws from — they are the twelve operators
 * a player can pick, and the same twelve the bots wear.
 */

/**
 * Callsigns for the scoreboard. Deterministic per id so a bot keeps its name
 * for its whole life and two bots on screen are never the same operator.
 */
const CALLSIGNS = [
  'VIPER', 'HAWKEYE', 'GHOST', 'REAPER', 'MAVERICK', 'BISHOP', 'CASTLE', 'DELTA',
  'ECHO', 'FALCON', 'GRIZZLY', 'HUSKY', 'IRONSIDE', 'JACKAL', 'KILO', 'LANCER',
];
export function callsignFor(id) {
  const base = CALLSIGNS[id % CALLSIGNS.length];
  const wrap = Math.floor(id / CALLSIGNS.length);
  return wrap ? `${base}-${wrap + 1}` : base;
}

/**
 * Turn an operator into the flat loadout the model builder reads.
 *
 * Two cache keys come out of this and they are NOT the same key, which is the
 * bug the previous version had. Geometry depends only on the gear an operator
 * wears, so it is cached per operator. Textures depend on the COLOURS, and the
 * same operator wears two different sets of them — team colours in bomb
 * defusal, its own in deathmatch. Keying both off one index meant a red and a
 * blue soldier who happened to draw the same palette index shared a cached camo
 * texture, and one of them silently wore the other side's colours.
 */
export function loadoutFor(id, team = null, { operator = null, ffa = false } = {}) {
  const op = operator ?? operatorFor(
    ffa ? id % ALL_OPERATORS.length : id,
    { team: team ?? 'red', ffa });
  return {
    ...op.gear,
    operator: op,
    // Geometry: gear only.
    key: op.id,
    // Textures: which colours, on which operator.
    paletteKey: `${op.id}:${ffa ? 's' : 't'}`,
    colors: paletteFor(op, ffa),
  };
}

/* ============================================================ model build == */

const _geoCache = new Map();

/**
 * Author the whole soldier in model space, bucketed by material, then merge
 * each bucket into one skinned geometry. Cached per loadout key so respawns
 * are free.
 */
function buildSoldierGeometry(lo) {
  const cached = _geoCache.get(lo.key);
  if (cached) return cached;

  const B = { cloth: [], gear: [], skin: [], dark: [], metal: [], eye: [] };
  const add = (bucket, geo, bones, opts) => {
    B[bucket].push(skinPiece(geo, bones, opts));
    return geo;
  };
  const at = (geo, x, y, z, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1) => {
    if (sx !== 1 || sy !== 1 || sz !== 1) geo.scale(sx, sy, sz);
    if (rx) geo.rotateX(rx);
    if (ry) geo.rotateY(ry);
    if (rz) geo.rotateZ(rz);
    geo.translate(x, y, z);
    return geo;
  };

  /*
   * Build, in four numbers rather than one.
   *
   * A single `build` multiplier only ever produces a scaled copy of the same
   * person. Splitting width from depth and torso from limbs is what lets one
   * operator be broad and shallow and another deep and narrow, which is the
   * difference a player can actually see in a silhouette.
   */
  const body = lo.operator?.body ?? {};
  const bw = lo.build * (body.width ?? 1);       // torso breadth
  const bd = lo.build * (body.depth ?? 1);       // torso depth, front to back
  const la = body.arm ?? 1;                      // arm girth
  const lg = body.leg ?? 1;                      // leg girth
  const fh = lo.operator?.face ?? {};
  const fw = fh.width ?? 1;                      // cranium width
  const fj = fh.jaw ?? 1;                        // jaw width and squareness
  const fl = fh.len ?? 1;                        // front-to-back length

  /* ---- torso ------------------------------------------------------------ */
  /*
   * One lofted mass from pelvis to yoke, replacing three stacked boxes, two
   * deltoid spheres and a trapezius wedge.
   *
   * The three boxes were the single worst thing about the model: a torso of
   * constant depth with hard vertical edges, which is why the silhouette read
   * as a slab from every angle. What a torso needs is a waist that pinches, a
   * ribcage that flares, and a depth that changes with height — and those are
   * the three things a box cannot have. Roundness climbs from 2.5 at the waist
   * to 3.0 at the chest because a ribcage really is closer to a rounded
   * rectangle than to an ellipse; that is what gives the chest a front plane
   * for the carrier to sit against.
   *
   * `bd` scales depth separately from width so a heavy operator reads deep in
   * profile, not just wide from the front — a build multiplier applied to both
   * only ever makes a bigger version of the same person.
   */
  const TORSO_B = ['hips', 'spine', 'chest', 'neck', 'clavL', 'clavR', 'shL', 'shR', 'hipL', 'hipR'];
  add('cloth', loft([
    { y: 0.895, rx: 0.126 * bw, rz: 0.092 * bd, round: 2.6 },
    { y: 0.965, rx: 0.141 * bw, rz: 0.099 * bd, round: 2.6 },
    { y: 1.055, rx: 0.129 * bw, rz: 0.092 * bd, round: 2.5 },   // waist
    { y: 1.160, rx: 0.147 * bw, rz: 0.102 * bd, round: 2.7 },
    { y: 1.270, rx: 0.170 * bw, rz: 0.113 * bd, round: 2.9 },   // lat flare
    { y: 1.360, rx: 0.179 * bw, rz: 0.112 * bd, round: 3.0 },
    { y: 1.432, rx: 0.147 * bw, rz: 0.098 * bd, round: 2.8 },   // yoke
    // The trapezius rises toward the neck. Without it the torso stopped flat
    // at the collar and 12 cm of bare neck stood up out of it like a bottle.
    { y: 1.468, rx: 0.104 * bw, rz: 0.080 * bd, round: 2.5 },
  ], { radial: 24, rings: 4 }), TORSO_B);

  /* ---- plate carrier ---------------------------------------------------- */
  // The carrier has to be weighted like the cloth underneath it, or the plates
  // separate from the torso the moment the spine bends.
  const V = ['hips', 'spine', 'chest'];
  /*
   * The carrier WRAPS the ribs. It used to be a front slab, a back slab and a
   * midriff box — a sandwich board hung on a torso, with four hard vertical
   * corners that were the most obviously artificial edges on the whole model.
   *
   * A real plate carrier is a continuous loop: front plate, cummerbund round
   * the sides, back plate. One closed loft is that loop, and it also makes the
   * three separate pieces unnecessary. Roundness runs 3.6-4.4 rather than the
   * body's 2.5-3.0, because armour genuinely is flatter across the chest than
   * flesh is — that flat front is what the pouches sit on.
   */
  add('gear', loft([
    { y: 1.075, rx: 0.152 * bw, rz: 0.110 * bd, round: 3.4 },
    { y: 1.150, rx: 0.161 * bw, rz: 0.118 * bd, round: 4.0 },
    { y: 1.270, rx: 0.178 * bw, rz: 0.126 * bd, round: 4.4 },
    { y: 1.350, rx: 0.185 * bw, rz: 0.124 * bd, round: 4.4 },
    { y: 1.402, rx: 0.170 * bw, rz: 0.112 * bd, round: 3.8 },
  ], { radial: 26, rings: 4 }), V);
  // Shoulder straps over the deltoids.
  for (const s of [1, -1]) {
    add('gear', at(rbox(0.105, 0.062, 0.315, 0.026, 3), s * 0.112 * bw, 1.416, -0.005, 0, 0, s * 0.16),
      ['chest', 'clavL', 'clavR']);
    add('gear', at(rbox(0.10, 0.05, 0.07, 0.018, 3), s * 0.105 * bw, 1.40, 0.115 * bd), ['chest']);
  }
  // The cummerbund is part of the shell loft above now; what is left is the
  // webbing band across it, which is what actually reads as a cummerbund.
  for (const s of [1, -1]) {
    add('dark', at(rbox(0.022, 0.105, 0.150, 0.010, 3), s * 0.181 * bw, 1.215, -0.004), ['chest', 'spine']);
  }
  // Front mag pouches.
  const mc = lo.magCount;
  for (let i = 0; i < mc; i++) {
    const x = (i - (mc - 1) / 2) * 0.095;
    add('gear', at(rbox(0.086, 0.145, 0.078, 0.030, 4), x, 1.17, 0.168), V);
    add('dark', at(rbox(0.074, 0.022, 0.032, 0.010, 3), x, 1.243, 0.186), V);
  }
  // Admin / radio / utility.
  add('gear', at(rbox(0.15, 0.095, 0.062, 0.026, 4), 0.02, 1.335, 0.162), ['chest']);
  add('gear', at(rbox(0.10, 0.165, 0.075, 0.028, 4), -0.095, 1.30, -0.168), ['chest']);
  if (lo.antenna) {
    add('dark', at(cyl(0.005, 0.007, 0.42, 6), -0.095, 1.56, -0.175, 0.18, 0, 0.05), ['chest']);
    add('dark', at(sph(0.011, 8, 6), -0.10, 1.76, -0.10), ['chest']);
  }
  add('gear', at(rbox(0.075, 0.115, 0.115, 0.025, 3), 0.185 * bw, 1.16, -0.03), ['spine', 'hips']);
  if (lo.dumpPouch) {
    add('gear', at(rbox(0.10, 0.15, 0.10, 0.035, 3), -0.185 * bw, 1.05, -0.06), ['hips']);
  }
  // Belt + buckle.
  add('dark', at(rbox(0.325 * bw, 0.055, 0.235, 0.022, 3), 0, 1.005, 0), ['hips']);
  add('metal', at(rbox(0.055, 0.045, 0.03, 0.01, 2), 0, 1.005, 0.12), ['hips']);
  // Grenades.
  add('dark', at(cyl(0.026, 0.03, 0.075, 10), 0.11, 1.245, 0.163), ['chest']);
  add('dark', at(cyl(0.026, 0.03, 0.075, 10), -0.115, 1.245, 0.163), ['chest']);
  if (lo.shoulderPatch) {
    add('dark', at(rbox(0.05, 0.035, 0.012, 0.004, 2), 0.19, 1.34, 0.055), ['shL']);
  }

  /* ---- head ------------------------------------------------------------- */
  const H = ['head', 'neck'];
  // Neck: wider at the base where the sternocleidomastoid lands, narrowing
  // under the jaw. A straight cylinder read as a pipe holding up a head.
  add('skin', loft([
    { y: 1.430, rx: 0.060, rz: 0.058, z: 0.000 },
    { y: 1.480, rx: 0.052, rz: 0.052, z: 0.003 },
    { y: 1.540, rx: 0.047, rz: 0.049, z: 0.004 },
  ], { radial: 16, rings: 3 }), ['neck', 'chest', 'head']);
  /*
   * Skull and jaw: ONE loft from the chin to the crown.
   *
   * This was a squashed sphere with a mid-face box, a jaw box and two cheek
   * wedges stacked in front of it — four masses whose flat fronts and hard top
   * edges are what made the face read as a pile of plates from the side.
   *
   * The constraint that survived from that version, and the reason the numbers
   * look fussy: every section's FRONT (z + rz) must stay at or behind 0.064,
   * because the authored face features — brow at 0.088, nose tip at 0.095, the
   * eye recess at 0.072 — are built forward of the skull, and a skull that
   * reaches past them swallows the recess and takes the eyes with it. The
   * widths are bounded the other way, by the balaclava that has to cover the
   * jaw without the cheeks punching through it.
   */
  /*
   * The skull, at loft resolution high enough to sculpt.
   *
   * 32 radial by 5 rings is about 2 500 triangles for the head alone — roughly
   * an eighth of the whole soldier, spent here because the face is the one
   * place a player looks at a body and decides whether it is a person.
   */
  const HEAD_RADIAL = 32, HEAD_RINGS = 5;
  const headSections = [
    { y: 1.535, rx: 0.050 * fj, rz: 0.044 * fl, z: 0.020, round: 2.5 },   // chin
    { y: 1.558, rx: 0.061 * fj, rz: 0.054 * fl, z: 0.012, round: 2.7 },   // jaw
    { y: 1.582, rx: 0.069 * fj, rz: 0.062 * fl, z: 0.002, round: 2.6 },
    { y: 1.601, rx: 0.075 * fw, rz: 0.068 * fl, z: -0.004, round: 2.5 },  // cheekbone
    { y: 1.634, rx: 0.086 * fw, rz: 0.076 * fl, z: -0.012, round: 2.4 },  // eye line
    { y: 1.668, rx: 0.085 * fw, rz: 0.077 * fl, z: -0.014, round: 2.3 },  // brow
    { y: 1.700, rx: 0.081 * fw, rz: 0.074 * fl, z: -0.017, round: 2.2 },
    { y: 1.722, rx: 0.070 * fw, rz: 0.064 * fl, z: -0.019, round: 2.1 },
    { y: 1.737, rx: 0.044 * fw, rz: 0.042 * fl, z: -0.020, round: 2.0 },  // crown
  ];
  const skull = loft(headSections, { radial: HEAD_RADIAL, rings: HEAD_RINGS });

  /*
   * The face, as displacement rather than as parts.
   *
   * Front-to-back order still matters and is still the thing that makes a face
   * read: the brow is proud, the eye sits in its own shadow behind it, the nose
   * is proud again. What changed is that these are now pushes on one continuous
   * surface, so the brow flows into the forehead and the socket flows into the
   * cheek instead of each one presenting its own silhouette and its own hard
   * edge against the skin.
   *
   * `fb` scales the ridges with the operator's brow so a heavy-set face reads
   * heavy-set from across a room, where nothing smaller survives.
   */
  const fb = fh.brow ?? 1;
  sculpt(skull, [
    // Brow ridge, and the glabella between the two halves. Deep enough to cast
    // its own shadow at a raking sun angle — a brow you cannot see the shadow
    // of is not doing the job a brow does.
    { x: 0.031, y: 1.657, z: 0.046, rx: 0.044, ry: 0.020, rz: 0.050,
      amount: 0.018 * fb, dir: [0, 0, 1], mirror: true },
    { y: 1.653, z: 0.048, rx: 0.017, ry: 0.016, rz: 0.042, amount: 0.009 * fb, dir: [0, 0, 1] },
    // Eye socket: behind the brow, cut deep, with the outer corner deeper than
    // the inner one so the eye sits in a wedge rather than a dimple.
    { x: 0.033, y: 1.634, z: 0.044, rx: 0.034, ry: 0.020, rz: 0.042,
      amount: -0.024, dir: [0, 0, 1], mirror: true },
    { x: 0.046, y: 1.636, z: 0.038, rx: 0.020, ry: 0.016, rz: 0.034,
      amount: -0.010, dir: [0, 0, 1], mirror: true },
    // Lower lid, which is what stops the socket reading as a hole.
    { x: 0.031, y: 1.622, z: 0.048, rx: 0.026, ry: 0.008, rz: 0.034,
      amount: 0.007, dir: [0, 0, 1], mirror: true },
    // Nose: bridge, tip, nostril flare. The tip carries the most displacement
    // on the whole face, and the bridge has to reach the brow or the nose
    // starts halfway down the face and reads as a blob.
    { y: 1.648, z: 0.046, rx: 0.013, ry: 0.024, rz: 0.038, amount: 0.014, dir: [0, 0, 1] },
    { y: 1.622, z: 0.046, rx: 0.014, ry: 0.026, rz: 0.038, amount: 0.024, dir: [0, 0, 1] },
    { y: 1.600, z: 0.046, rx: 0.017, ry: 0.018, rz: 0.036, amount: 0.034, dir: [0, 0, 1] },
    { x: 0.016, y: 1.591, z: 0.042, rx: 0.015, ry: 0.012, rz: 0.030,
      amount: 0.018, dir: [0, 0, 1], mirror: true },
    // Cheekbone, and the hollow under it.
    { x: 0.054, y: 1.606, z: 0.024, rx: 0.036, ry: 0.028, rz: 0.046,
      amount: 0.013, mirror: true },
    { x: 0.049, y: 1.574, z: 0.030, rx: 0.028, ry: 0.020, rz: 0.038,
      amount: -0.008, mirror: true },
    // Temple hollow — the pair of dents that make a skull read as bone.
    { x: 0.073, y: 1.662, z: 0.006, rx: 0.026, ry: 0.030, rz: 0.036,
      amount: -0.010, mirror: true },
    // Mouth: the line, the upper lip above it, the lower lip below.
    { y: 1.566, z: 0.044, rx: 0.032, ry: 0.007, rz: 0.032, amount: -0.009, dir: [0, 0, 1] },
    { y: 1.578, z: 0.046, rx: 0.024, ry: 0.010, rz: 0.030, amount: 0.008, dir: [0, 0, 1] },
    { y: 1.557, z: 0.044, rx: 0.022, ry: 0.009, rz: 0.030, amount: 0.006, dir: [0, 0, 1] },
    // Chin point and the crease above it.
    { y: 1.542, z: 0.040, rx: 0.023, ry: 0.016, rz: 0.032, amount: 0.010, dir: [0, 0, 1] },
    { y: 1.551, z: 0.042, rx: 0.020, ry: 0.006, rz: 0.028, amount: -0.005, dir: [0, 0, 1] },
    // Jaw angle, and the occiput at the back of the skull.
    { x: 0.061, y: 1.560, z: -0.012, rx: 0.026, ry: 0.024, rz: 0.036,
      amount: 0.010, mirror: true },
    { y: 1.686, z: -0.060, rx: 0.054, ry: 0.046, rz: 0.034, amount: 0.010, dir: [0, 0, -1] },
    // The ear's BASE, pushed out of the skull. The helix below is a separate
    // piece — it has to be, to have a rim — but without this it was an ellipsoid
    // floating beside the head with a visible gap under it, which is exactly
    // the bolted-on read the remodel is meant to remove.
    { x: 0.074, y: 1.618, z: -0.024, rx: 0.022, ry: 0.026, rz: 0.020,
      amount: 0.009, dir: [1, 0, 0], mirror: true },
    { x: 0.072, y: 1.616, z: -0.008, rx: 0.012, ry: 0.018, rz: 0.012,
      amount: -0.005, dir: [1, 0, 0], mirror: true },
  ]);
  weldSeam(skull, HEAD_RADIAL, (headSections.length - 1) * HEAD_RINGS + 1);
  add('skin', skull, H);

  for (const s of [1, -1]) {
    add('skin', at(sph(0.021, 14, 12), s * 0.079 * fw, 1.619, -0.024,
      0, s * 0.40, s * 0.14, 0.40, 1.12, 0.74), H); // ear
    /*
     * The eye: sclera, iris, and two lids.
     *
     * One dark sphere in a socket is a bead, and no amount of socket sculpting
     * fixes that — what says "eye" is the pale sclera against a dark iris, and
     * the LIDS cutting the sphere down to a slit. A whole eyeball on show is
     * the look of a doll.
     *
     * Real proportions, because they are cheap to honour: the ball is 24 mm
     * across and the opening between the lids about 9 mm, so roughly two
     * thirds of the sphere is covered. The lids are skin-material and overlap
     * the ball, so they read as continuous with the socket around them rather
     * than as two more applied parts.
     */
    const ex = s * 0.032, ey = 1.6335, ez = 0.038;
    /*
     * The ball is NARROWED in x, and so are the lids.
     *
     * The lids are caps cut at constant height, so between them the full
     * circumference of the ball is exposed — a band, not an almond, and from
     * the front that is a wide white stripe with a dot in it. Squeezing both
     * the ball and the lids to about 70% of their width turns that band into
     * a 17 x 10 mm opening, which is the shape of a real palpebral fissure to
     * within a millimetre or two and costs nothing.
     *
     * The alternative — tilting each lid's axis so the two rims intersect at
     * the corners, which is what real lids do — was worked through and does not
     * survive contact with a cap: tilting moves the front and the corners of
     * the rim in the same direction, so it never closes at the ends.
     */
    add('eye', at(sph(0.0120, 16, 12), ex, ey, ez, 0, 0, 0, 0.72, 1, 0.95), H);
    /*
     * Iris as a CONCENTRIC CAP, not a small sphere pushed forward.
     *
     * A sphere parked in front of the eyeball has to be positioned by hand so
     * that it pokes through, and the first attempt did not: its front sat at
     * z 0.0485 against a sclera front of 0.0494, so the iris was buried inside
     * the ball and the eye rendered as a blank white bead. A cap sharing the
     * ball's centre and three tenths of a millimetre larger is proud of it
     * everywhere by construction, and stays proud when the ball is squeezed,
     * because the same squeeze is applied to both.
     *
     * `dark` rather than `metal`: a metallic iris reads as a ball bearing.
     */
    add('dark', at(sphCap(0.0123, 0, 0.463).rotateX(Math.PI / 2),
      ex, ey, ez, 0, 0, 0, 0.72, 1, 0.95), H);
    /*
     * Lids as concentric caps, cut so the opening between them is about nine
     * millimetres, with the upper one low enough to clip the top of the iris —
     * an upper lid that clears the iris entirely is a stare — the real palpebral fissure, and about two fifths of the
     * ball. The tilt is a rotation of the whole cap, which keeps it concentric:
     * the outer corner of a human eye sits slightly higher than the inner one,
     * and without that an eye reads as a doll's.
     */
    add('skin', at(sphCap(0.0129, 0, 1.33), ex, ey, ez, 0.06, 0, s * 0.12, 0.80, 1, 1.02), H);
    add('skin', at(sphCap(0.0127, Math.PI - 1.123, 1.123), ex, ey, ez, 0.04, 0, s * 0.12, 0.80, 1, 1.02), H);
  }
  if (lo.beard) {
    add('dark', at(rbox(0.134, 0.072, 0.096, 0.034, 4), 0, 1.552, 0.016), H);
    add('dark', at(rbox(0.060, 0.022, 0.032, 0.008, 2), 0, 1.584, 0.066), H);   // moustache
  }
  if (lo.balaclava || lo.shemagh) {
    /*
     * The mask is the FACE, offset outwards — not a box in front of it.
     *
     * A rounded box was the last blocky thing on the head, and it is the one
     * eight of the twelve operators wear, so it was most of what "the faces are
     * still minecraftish" was pointing at. Resampling the skull's own profile
     * and pushing it out by six millimetres gives cloth that hugs the jaw and
     * the cheekbones, and sculpting the nose into it again means the mask wraps
     * over the bridge the way a real one does instead of flattening it.
     *
     * It also cannot clip: the surface underneath is the surface it was built
     * from, so "wide enough to cover the cheeks" stops being a number anybody
     * has to keep in sync.
     */
    const MASK_RADIAL = 28;
    // Top edge at 1.617: the eyeball's underside is at 1.6215, and at 1.628 the
    // mask was slicing through it and swallowing the lower lid. The ear (centre
    // 1.619) ends up half covered, which is what a balaclava over an ear does.
    const maskSections = profileBand(headSections, 1.496, 1.617, 8, 0.0065, 0.25);
    const mask = loft(maskSections, { radial: MASK_RADIAL, rings: 4 });
    sculpt(mask, [
      { y: 1.600, z: 0.050, rx: 0.020, ry: 0.020, rz: 0.040, amount: 0.030, dir: [0, 0, 1] },
      { x: 0.017, y: 1.591, z: 0.046, rx: 0.017, ry: 0.013, rz: 0.032,
        amount: 0.016, dir: [0, 0, 1], mirror: true },
      // A fold across the bridge of the nose, where a pulled-up mask creases.
      { y: 1.612, z: 0.046, rx: 0.030, ry: 0.007, rz: 0.036, amount: -0.005, dir: [0, 0, 1] },
      { y: 1.560, z: 0.044, rx: 0.034, ry: 0.010, rz: 0.034, amount: -0.004, dir: [0, 0, 1] },
    ]);
    weldSeam(mask, MASK_RADIAL, (maskSections.length - 1) * 4 + 1);
    add('gear', mask, H);
  }
  if (lo.shemagh) {
    add('gear', at(rbox(0.19, 0.10, 0.185, 0.06, 4), 0, 1.478, -0.006), ['neck', 'chest']);
    add('gear', at(rbox(0.075, 0.16, 0.05, 0.03, 3), 0.115, 1.44, 0.07, 0.2, 0, 0.25), ['neck', 'chest']);
  }
  if (lo.goggles) {
    // Over the eye recess, under the brim, strap running round the skull. The
    // lens is `metal` so it takes a hard specular streak — the single strongest
    // "this is a soldier" cue at 14 m, where no facial geometry survives.
    add('dark', at(rbox(0.176 * fw, 0.048, 0.058, 0.020, 4), 0, 1.636, 0.054), H);
    add('metal', at(rbox(0.152 * fw, 0.034, 0.018, 0.008, 3), 0, 1.637, 0.084), H);
    add('gear', at(rbox(0.230 * fw, 0.030, 0.19 * fl, 0.012, 3), 0, 1.642, -0.020), H);
  }

  /* ---- helmet ----------------------------------------------------------- */
  if (lo.helmet) {
    // High-cut ballistic helmet. The dome is a hemisphere that stops at the
    // brow line — the previous version was a full 104-degree sphere section
    // whose front rim hung 5 cm below the eyes, which buried the entire face
    // and turned the head into one featureless blob at any distance.
    const cover = lo.helmetCover ? 'cloth' : 'gear';
    const shell = new THREE.SphereGeometry(0.121, 22, 14, 0, Math.PI * 2, 0, Math.PI * 0.5);
    // Flatter than a hemisphere: a real ballistic shell is a low dome with a
    // slight front-to-back keel, not a beach ball. At 0.93 y-scale the close-up
    // read was "helmet-shaped balloon".
    add(cover, at(shell, 0, 1.674, -0.012, 0, 0, 0, 0.94 * fw, 0.80, 1.02 * fl), ['head']);
    // Crown keel — one long panel break so the dome highlight is not a single
    // unbroken sweep.
    add(cover, at(rbox(0.040, 0.020, 0.210, 0.009, 3), 0, 1.776, -0.014), ['head']);
    // Occiput and ear skirts: coverage drops behind and beside the head only.
    add(cover, at(rbox(0.198 * fw, 0.086, 0.112 * fl, 0.044, 4), 0, 1.650, -0.070 * fl), ['head']);
    for (const s of [1, -1]) {
      add(cover, at(rbox(0.034, 0.058, 0.128 * fl, 0.027, 4), s * 0.096 * fw, 1.648, -0.030, 0, 0, s * 0.08), ['head']);
    }
    // Front brim: a shallow visor that sits *above* the brow.
    add(cover, at(rbox(0.170 * fw, 0.026, 0.054, 0.013, 4), 0, 1.698, 0.072 * fl, -0.20), ['head']);
    // Shell edge trim reads as a real rim rather than a painted line.
    add('dark', at(cyl(0.116, 0.118, 0.015, 24), 0, 1.672, -0.012, 0, 0, 0, 0.98 * fw, 1, 1.06 * fl), ['head']);
    // ARC rails down each side.
    for (const s of [1, -1]) {
      add('dark', at(rbox(0.009, 0.019, 0.136 * fl, 0.004, 3), s * 0.1175 * fw, 1.700, -0.014, 0, 0, s * -0.14), ['head']);
      // Retention: chinstrap yoke down past the ear to the jaw.
      add('dark', at(rbox(0.007, 0.062, 0.009, 0.003, 3), s * 0.076, 1.596, -0.006, 0, 0, s * 0.22), ['head']);
      add('dark', at(rbox(0.007, 0.048, 0.009, 0.003, 3), s * 0.074, 1.600, -0.048, 0, 0, s * 0.16), ['head']);
      // Chin cup strap running under the jaw closes the retention system —
      // without it the yoke straps just stopped in mid air beside the face.
      add('dark', at(rbox(0.007, 0.042, 0.009, 0.003, 3), s * 0.050, 1.546, 0.024, 0.55, 0, s * 0.45), ['head']);
    }
    add('dark', at(rbox(0.078, 0.011, 0.024, 0.005, 3), 0, 1.528, 0.044), ['head']);
    if (lo.nvg) {
      // Shroud + folded mount, sitting on the brim rather than in front of it.
      add('dark', at(rbox(0.058, 0.026, 0.052, 0.010, 3), 0, 1.730, 0.074), ['head']);
      add('metal', at(rbox(0.030, 0.062, 0.030, 0.008, 2), 0, 1.764, 0.088, -0.30), ['head']);
      add('dark', at(rbox(0.072, 0.030, 0.026, 0.008, 2), 0, 1.786, 0.100, -0.30), ['head']);
    }
    if (lo.counterweight) {
      add('gear', at(rbox(0.115, 0.082, 0.058, 0.024, 3), 0, 1.688, -0.124), ['head']);
      add('dark', at(rbox(0.030, 0.022, 0.048, 0.006, 2), 0, 1.736, -0.104), ['head']);
    }
    // IR strobe / glint tab.
    add('metal', at(rbox(0.028, 0.013, 0.028, 0.005, 2), 0.050, 1.768, -0.062), ['head']);
  }

  /* ---- arms ------------------------------------------------------------- */
  for (const s of [1, -1]) {
    const sh = s > 0 ? 'shL' : 'shR', el = s > 0 ? 'elL' : 'elR';
    const wr = s > 0 ? 'wrL' : 'wrR', hn = s > 0 ? 'hnL' : 'hnR';
    const clav = s > 0 ? 'clavL' : 'clavR';
    const A = ['chest', clav, sh, el, wr];
    /*
     * Deltoid, bicep and elbow in ONE loft, and the forearm in a second.
     *
     * Two constant-radius capsules and a sphere at the elbow was three masses
     * pretending to be an arm: no deltoid, a bicep the same girth as a wrist,
     * and a ball at the joint that read as a bolt. The deltoid is now the top
     * of the upper-arm sweep, so it grows out of the arm instead of being
     * parked beside it, and the section at the elbow matches the forearm's
     * first section exactly — which is what closes the joint without a bulge.
     */
    add('cloth', loft([
      { y: 1.128, rx: 0.056 * la, rz: 0.055 * la, x: s * 0.190 },   // elbow
      { y: 1.180, rx: 0.060 * la, rz: 0.058 * la, x: s * 0.190 },
      { y: 1.255, rx: 0.071 * la, rz: 0.068 * la, x: s * 0.190 },   // bicep
      { y: 1.330, rx: 0.081 * la, rz: 0.077 * la, x: s * 0.188 },   // deltoid belly
      { y: 1.400, rx: 0.076 * la, rz: 0.072 * la, x: s * 0.182 },
      { y: 1.432, rx: 0.058 * la, rz: 0.056 * la, x: s * 0.172 },   // into the yoke
    ], { radial: 18, rings: 3 }), A);
    /*
     * The forearm now TAPERS INTO the glove instead of butting against it. It
     * used to finish at a 80 x 86 mm wrist, wider than the hand below it, so
     * the arm got thinner and then stopped — the silhouette of an amputation.
     * A wrist is the narrowest point on the whole arm; the hand flares back out
     * of it, and that flare is what tells the eye where the arm ends.
     */
    add('cloth', loft([
      { y: 0.905, rx: 0.034 * la, rz: 0.037 * la, x: s * 0.190 },   // wrist
      { y: 0.945, rx: 0.042 * la, rz: 0.044 * la, x: s * 0.190 },
      { y: 1.000, rx: 0.053 * la, rz: 0.051 * la, x: s * 0.190 },
      { y: 1.055, rx: 0.061 * la, rz: 0.058 * la, x: s * 0.190 },   // brachioradialis
      { y: 1.128, rx: 0.056 * la, rz: 0.055 * la, x: s * 0.190 },   // elbow, matched
    ], { radial: 18, rings: 3 }), [sh, el, wr, hn]);
    if (lo.elbowPads) {
      add('gear', at(rbox(0.088, 0.085, 0.09, 0.03, 3), s * 0.192, 1.132, 0.012), [sh, el]);
    }
    /*
     * Glove: gauntlet, palm, knuckle plate, four fingers and a thumb.
     *
     * Rebuilt after "where are hands". The old glove was a 58 x 98 x 80 mm
     * bevelled box with four 40 mm nubs hanging off the bottom — NARROWER
     * across than the wrist above it, so the arm tapered to a point and
     * stopped. At operator-picker distance a dark box against a dark trouser
     * leg is not a hand, and that is exactly what it read as.
     *
     * The proportions come from the FIRST-PERSON glove in Weapon.js, which is
     * signed off and was solved against BO6/BOCW reference: palm 82 mm wrist
     * to knuckle, 85 mm across, 55 mm thick, middle finger 102 mm over three
     * phalanges, thumb 78 mm. That is a 184 mm hand. The old one was 138 mm of
     * which 98 was an undifferentiated block. Reusing the numbers keeps the
     * soldier you see across the map and the hands you see on your own weapon
     * as the same pair of gloves.
     *
     * Handedness: s = +1 is the LEFT arm. A hand hanging at the side puts the
     * palm against the thigh, so the BACK of the hand faces outboard (+x on
     * the left), the index finger sits FORWARD (+z), the little finger back,
     * and the thumb rides the front of the palm. Every offset below is written
     * in those terms.
     */
    const hx = s * 0.190;
    const hg = (1 + la) / 2;          // hands track arm girth, but only half as hard
    // Gauntlet — the glove cuff closing over the sleeve. Without it the sleeve
    // simply ends; with it the sleeve ends IN something.
    add('gear', loft([
      { y: 0.878, rx: 0.037 * hg, rz: 0.041 * hg, x: hx, round: 3.0 },
      { y: 0.900, rx: 0.042 * hg, rz: 0.046 * hg, x: hx, round: 3.2 },
      { y: 0.928, rx: 0.041 * hg, rz: 0.045 * hg, x: hx, round: 3.2 },
      { y: 0.946, rx: 0.034 * hg, rz: 0.037 * hg, x: hx, round: 3.0 },
    ], { radial: 14, rings: 2 }), [wr, hn]);
    /*
     * Palm as a loft, not a box: it has to be widest across the metacarpals,
     * thinnest at the wrist and rolled off at the knuckle row. Roundness stays
     * near 2.2-2.7 — a palm is an ellipse in section, and the first pass at
     * 2.6-3.4 plus a full-width knuckle plate turned the back of the hand into
     * one flat card.
     */
    add('dark', loft([
      { y: 0.806, rx: 0.026 * hg, rz: 0.040 * hg, x: hx, z: 0.012, round: 2.6 },
      { y: 0.822, rx: 0.031 * hg, rz: 0.045 * hg, x: hx, z: 0.010, round: 2.7 },
      { y: 0.845, rx: 0.033 * hg, rz: 0.045 * hg, x: hx, z: 0.008, round: 2.6 },
      { y: 0.868, rx: 0.033 * hg, rz: 0.041 * hg, x: hx, z: 0.006, round: 2.4 },
      { y: 0.894, rx: 0.031 * hg, rz: 0.035 * hg, x: hx, z: 0.004, round: 2.2 },
    ], { radial: 16, rings: 3 }), [wr, hn]);
    // Thenar pad: the thumb muscle on the palm side. Without it the thumb is
    // bolted to a flat card.
    add('dark', at(rbox(0.024 * hg, 0.048 * hg, 0.036 * hg, 0.011, 3),
      hx - s * 0.018 * hg, 0.858, 0.020), [hn, wr]);
    // Knuckle guard on the DORSAL face — a distinct plate is what says "back of
    // the hand" at a glance, and it is the one feature on a glove that still
    // reads at 30 m.
    add('gear', at(rbox(0.010, 0.026 * hg, 0.050 * hg, 0.005, 3),
      hx + s * 0.026 * hg, 0.821, 0.010), [hn]);

    /*
     * Fingers: three segments each, because a relaxed hand creases at both
     * joints and one straight capsule cannot show either.
     *
     * `digit` walks a capsule from a point along an Euler the geometry can
     * actually be placed with: Z tilts the axis sideways, X swings it down and
     * forward, and the order three.js applies them in (XYZ) makes the
     * direction (-sin g, cos g cos a, cos g sin a).
     */
    const digit = (px, py, pz, r, L, a, gz, bones) => {
      const cg = Math.cos(gz), sg = Math.sin(gz);
      const ax = Math.PI - a;
      const dx = -sg, dy = cg * Math.cos(ax), dz = cg * Math.sin(ax);
      const tot = L + 2 * r;
      add('dark', at(caps(r, L, 4, 10),
        px + dx * tot * 0.5, py + dy * tot * 0.5, pz + dz * tot * 0.5,
        ax, 0, gz), bones);
      // Hand the NEXT segment a point one radius short of the tip, so the two
      // capsules overlap instead of kissing. Butted end to end they leave a
      // visible pinch at every joint and the finger reads as three beads.
      return [px + dx * (tot - r), py + dy * (tot - r), pz + dz * (tot - r)];
    };
    // Across-palm offsets, MCP heights and thicknesses, all straight off the
    // viewmodel glove. The knuckle row ARCS — index and little finger sit back
    // — which is the difference between a hand and a paddle in silhouette.
    const FZ = [0.034, 0.011, -0.011, -0.034];
    const FY = [0.811, 0.808, 0.810, 0.817];
    const FR = [0.0086, 0.0090, 0.0084, 0.0074];
    const FL = [
      [0.044, 0.029, 0.022],
      [0.047, 0.032, 0.023],
      [0.044, 0.030, 0.022],
      [0.036, 0.024, 0.020],
    ];
    for (let f = 0; f < 4; f++) {
      const r0 = FR[f] * hg;
      /*
       * Cumulative curl at each joint, measured from the forearm axis. A
       * relaxed hand is NOT a fist and it is not a flat paddle either: the
       * proximal phalanx drops about 35 degrees, the middle carries the finger
       * past horizontal and the distal tucks the tip back toward the palm, so
       * the fingertips end up in front of the palm rather than out in the air.
       * Two straight segments at shallow angles — the first attempt — gave a
       * hand permanently POINTING forward, which is worse than no hand.
       */
      const ang = [0.58 + f * 0.05, 1.62 + f * 0.07, 2.16 + f * 0.08];
      const gz = s * (-0.02 - f * 0.035);          // tips splay very slightly
      let px = hx, py = FY[f], pz = 0.012 + FZ[f] * hg;
      for (let i = 0; i < 3; i++) {
        const r = r0 * (1 - i * 0.10);
        [px, py, pz] = digit(px, py, pz, r, Math.max(0.004, FL[f][i] * hg - 2 * r),
          ang[i], gz, [hn]);
      }
    }
    /*
     * Thumb: three segments off the thenar pad, running down and FORWARD
     * across the front of the palm, leaning only slightly inboard. A thumb
     * that sticks out sideways is the loudest tell of a modelled hand; the old
     * one was a single 30 mm capsule and was not visible from any angle.
     */
    {
      let px = hx - s * 0.004 * hg, py = 0.880, pz = 0.030;
      const tl = [0.035, 0.031, 0.024], tr = [0.0125, 0.0115, 0.0102];
      const ta = [0.62, 0.96, 1.28];
      for (let i = 0; i < 3; i++) {
        const r = tr[i] * hg;
        [px, py, pz] = digit(px, py, pz, r, Math.max(0.004, tl[i] * hg - 2 * r),
          ta[i], s * (0.16 + i * 0.06), [hn, wr]);
      }
    }
  }

  /* ---- legs ------------------------------------------------------------- */
  for (const s of [1, -1]) {
    const hp = s > 0 ? 'hipL' : 'hipR', kn = s > 0 ? 'knL' : 'knR';
    const an = s > 0 ? 'anL' : 'anR', to = s > 0 ? 'toL' : 'toR';
    /*
     * Thigh and calf as lofts. Three capsules gave a leg of constant girth
     * from hip to ankle with a seam at each join; a leg is a quadriceps that
     * swells above the knee, a knee that is the narrowest point of the upper
     * leg, and a gastrocnemius that sits high and to the BACK — which is why
     * the calf sections carry more depth than width and their centres drift
     * backwards.
     */
    add('cloth', loft([
      { y: 0.495, rx: 0.067 * lg, rz: 0.070 * lg, x: s * 0.105, z: 0.004 },   // knee
      { y: 0.560, rx: 0.073 * lg, rz: 0.076 * lg, x: s * 0.105, z: 0.004 },
      { y: 0.700, rx: 0.087 * lg, rz: 0.088 * lg, x: s * 0.105, z: 0.004 },
      { y: 0.850, rx: 0.100 * lg, rz: 0.099 * lg, x: s * 0.105, z: 0.002 },   // quad
      { y: 0.955, rx: 0.098 * lg, rz: 0.096 * lg, x: s * 0.105, z: 0 },
    ], { radial: 20, rings: 4 }), ['hips', hp, kn]);
    add('cloth', loft([
      { y: 0.135, rx: 0.046 * lg, rz: 0.051 * lg, x: s * 0.105, z: 0.004 },   // ankle
      { y: 0.225, rx: 0.058 * lg, rz: 0.064 * lg, x: s * 0.105, z: 0.000 },
      { y: 0.340, rx: 0.072 * lg, rz: 0.083 * lg, x: s * 0.105, z: -0.008 },  // gastroc
      { y: 0.425, rx: 0.075 * lg, rz: 0.081 * lg, x: s * 0.105, z: -0.004 },
      { y: 0.495, rx: 0.069 * lg, rz: 0.071 * lg, x: s * 0.105, z: 0.004 },   // knee, matched
    ], { radial: 20, rings: 4 }), [kn, an]);
    // Bloused trouser cuff over the boot top.
    add('cloth', at(rbox(0.115, 0.075, 0.125, 0.045, 3), s * 0.105, 0.215, 0.004), [an, kn]);
    if (lo.kneePads) {
      add('gear', at(rbox(0.112, 0.145, 0.10, 0.042, 4), s * 0.105, 0.505, 0.048), [hp, kn]);
    }
    // Cargo pocket on the thigh.
    add('gear', at(rbox(0.05, 0.135, 0.105, 0.03, 3), s * 0.158, 0.685, 0.015), [hp]);
    /*
     * Boot: an ankle cuff and a shaped LAST, not four stacked cuboids.
     *
     * The foot loft is swept along Y and then laid down with a quarter turn
     * about X, so its sections run heel to toe and each one is the boot's real
     * cross-section at that point: narrow and deep at the heel, widest at the
     * ball, tapering and dropping at the toe. Roundness stays high (4-5)
     * because a boot IS boxy — but boxy with a taper and a rounded edge, which
     * is a different thing from a box.
     */
    add('dark', loft([
      { y: 0,     rx: 0.048, rz: 0.038, z: 0.004, round: 3.4 },   // heel
      { y: 0.050, rx: 0.055, rz: 0.044, z: 0.000, round: 4.0 },
      { y: 0.140, rx: 0.059, rz: 0.045, z: -0.002, round: 4.6 },  // ball
      { y: 0.215, rx: 0.054, rz: 0.038, z: -0.008, round: 4.4 },
      { y: 0.262, rx: 0.038, rz: 0.026, z: -0.014, round: 3.6 },  // toe
    ], { radial: 18, rings: 3 }).rotateX(Math.PI / 2).translate(s * 0.105, 0.070, -0.062),
    [an, to]);
    // Sole: the same last, flattened and dropped, so the welt line follows the
    // boot instead of cutting across it.
    add('dark', loft([
      { y: 0,     rx: 0.050, rz: 0.016, z: 0.004, round: 4.0 },
      { y: 0.050, rx: 0.057, rz: 0.018, z: 0.000, round: 4.6 },
      { y: 0.140, rx: 0.061, rz: 0.018, z: -0.002, round: 5.0 },
      { y: 0.225, rx: 0.056, rz: 0.016, z: -0.006, round: 4.6 },
      { y: 0.268, rx: 0.040, rz: 0.012, z: -0.010, round: 4.0 },
    ], { radial: 16, rings: 2 }).rotateX(Math.PI / 2).translate(s * 0.105, 0.026, -0.062),
    [an, to]);
    // Ankle cuff, tapering up into the bloused trouser.
    add('dark', loft([
      { y: 0.098, rx: 0.058, rz: 0.062, x: s * 0.105, z: 0.004, round: 3.0 },
      { y: 0.150, rx: 0.056, rz: 0.058, x: s * 0.105, z: 0.004, round: 2.8 },
      { y: 0.200, rx: 0.054, rz: 0.055, x: s * 0.105, z: 0.002, round: 2.6 },
    ], { radial: 16, rings: 2 }), [an, kn]);
    add('gear', at(rbox(0.095, 0.015, 0.058, 0.005, 2), s * 0.105, 0.054, 0.168), [to]);
    // Laces / speed-hook panel breaks the boot into two values. Kept on the
    // dark material: on `gear` it took the lifted nylon albedo and read as a
    // bare ankle showing between the bloused cuff and the boot.
    add('dark', at(rbox(0.062, 0.070, 0.030, 0.010, 2), s * 0.105, 0.128, 0.066), [an]);
  }
  if (lo.holster) {
    add('gear', at(rbox(0.07, 0.185, 0.125, 0.03, 3), -0.17, 0.70, 0.02), ['hipR']);
    add('metal', at(rbox(0.028, 0.095, 0.08, 0.012, 2), -0.17, 0.79, 0.015), ['hipR']);
  }

  /* ---- slung secondary -------------------------------------------------- */
  if (lo.secondary) {
    const g = [];
    g.push(at(rbox(0.05, 0.075, 0.42, 0.018, 3), 0, 0, 0));
    g.push(at(cyl(0.012, 0.012, 0.26, 8), 0, 0.012, -0.32, Math.PI / 2));
    g.push(at(rbox(0.035, 0.13, 0.06, 0.014, 2), 0, -0.085, 0.13, 0.25));
    g.push(at(rbox(0.045, 0.05, 0.15, 0.014, 2), 0, 0.0, 0.28));
    const merged = mergeGeometries(g, false);
    merged.rotateY(0.35); merged.rotateZ(0.55); merged.rotateX(-0.12);
    merged.translate(-0.02, 1.20, -0.20);
    add('metal', merged, ['chest', 'spine']);
    // Sling.
    add('dark', at(rbox(0.03, 0.36, 0.02, 0.008, 3), 0.12, 1.29, -0.03, 0, 0, -0.55), ['chest']);
    add('dark', at(rbox(0.03, 0.30, 0.02, 0.008, 3), -0.12, 1.30, 0.06, 0, 0, 0.45), ['chest']);
  }

  const out = {};
  for (const k of Object.keys(B)) {
    if (!B[k].length) { out[k] = null; continue; }
    const merged = mergeGeometries(B[k], false);
    // Per-primitive UVs make the camo restart on every capsule, which reads as
    // banding on the limbs. Re-project in model space instead.
    if (k !== 'skin' && k !== 'eye') projectUVs(merged, k === 'cloth' ? 2.6 : k === 'gear' ? 4.0 : 7.0);
    out[k] = merged;
    for (const g of B[k]) g.dispose?.();
  }
  _geoCache.set(lo.key, out);
  return out;
}

/** Cheap triplanar UV assignment — the pattern is noise, so seams vanish. */
function projectUVs(geo, scale) {
  const pos = geo.attributes.position;
  const nor = geo.attributes.normal;
  const uv = geo.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const nx = Math.abs(nor.getX(i)), ny = Math.abs(nor.getY(i)), nz = Math.abs(nor.getZ(i));
    let u, v;
    if (ny > nx && ny > nz) { u = x; v = z; }
    else if (nx > nz) { u = z; v = y; }
    else { u = x; v = y; }
    uv.setXY(i, u * scale, v * scale);
  }
  uv.needsUpdate = true;
}

/* ---------------------------------------------------------------- rifle --- */

/** Carbine, built with the muzzle along +Z to match the model's forward. */
function buildRifle() {
  if (_geoCache.has('__rifle')) return _geoCache.get('__rifle');
  const dark = [], metal = [];
  const at = (g, x, y, z, rx = 0, ry = 0, rz = 0) => {
    if (rx) g.rotateX(rx); if (ry) g.rotateY(ry); if (rz) g.rotateZ(rz);
    g.translate(x, y, z); return g;
  };
  // Receiver / handguard / barrel.
  metal.push(at(rbox(0.058, 0.098, 0.245, 0.014, 3), 0, 0, 0.02));
  metal.push(at(rbox(0.054, 0.074, 0.262, 0.018, 3), 0, 0.004, 0.268));
  metal.push(at(cyl(0.0130, 0.0130, 0.13, 10), 0, 0.006, 0.44, Math.PI / 2));
  metal.push(at(cyl(0.024, 0.024, 0.058, 10), 0, 0.006, 0.50, Math.PI / 2));
  // Pistol grip / magazine / stock.
  dark.push(at(rbox(0.036, 0.145, 0.055, 0.016, 3), 0, -0.088, -0.025, -0.30));
  dark.push(at(rbox(0.040, 0.165, 0.072, 0.016, 3), 0, -0.120, 0.085, 0.12));
  dark.push(at(rbox(0.054, 0.082, 0.180, 0.022, 3), 0, -0.012, -0.178));
  dark.push(at(rbox(0.050, 0.118, 0.044, 0.016, 3), 0, -0.030, -0.272));
  dark.push(at(rbox(0.030, 0.045, 0.11, 0.012, 2), 0, -0.048, -0.14)); // cheek riser
  // Optic sitting forward on the rail, plus rail slats.
  dark.push(at(rbox(0.042, 0.058, 0.092, 0.011, 3), 0, 0.072, 0.075));
  metal.push(at(cyl(0.023, 0.023, 0.088, 10), 0, 0.096, 0.075, Math.PI / 2));
  dark.push(at(rbox(0.026, 0.026, 0.026, 0.006, 2), 0, 0.090, 0.122));
  for (let i = 0; i < 7; i++) dark.push(at(rbox(0.048, 0.008, 0.012, 0.003, 1), 0, 0.040, 0.17 + i * 0.031));
  // Angled foregrip + laser box + sling loop.
  dark.push(at(rbox(0.030, 0.085, 0.032, 0.012, 2), 0, -0.062, 0.285, 0.22));
  dark.push(at(rbox(0.030, 0.032, 0.058, 0.008, 2), -0.036, 0.016, 0.21));
  metal.push(at(rbox(0.010, 0.03, 0.012, 0.004, 1), 0.030, -0.02, 0.14));

  const out = {
    dark: mergeGeometries(dark, false),
    metal: mergeGeometries(metal, false),
  };
  projectUVs(out.dark, 7.0);
  projectUVs(out.metal, 7.0);
  _geoCache.set('__rifle', out);
  return out;
}

/* ----------------------------------------------------------- assembly ----- */

/** Exposed for debug/model's primitive bench. Not used by the game. */
export { loft as __loft };

export function makeSoldier(lo) {
  const pal = lo.colors;
  const g = new THREE.Group();
  // The model is authored with +Z forward (so the face reads in front views),
  // while the rig's forward is -Z. One flip group reconciles the two.
  const rig = new THREE.Group();
  rig.rotation.y = Math.PI;
  g.add(rig);
  const { map, list } = buildSkeletonBones();
  rig.add(map.root);

  const mats = {
    cloth: new THREE.MeshStandardMaterial({
      map: camoTex(lo.paletteKey, pal.camo), roughness: 0.9, metalness: 0.0,
    }),
    gear: new THREE.MeshStandardMaterial({
      map: nylonTex(lo.paletteKey, pal.gear), roughness: 0.72, metalness: 0.05,
    }),
    skin: new THREE.MeshStandardMaterial({
      map: skinTex(lo.paletteKey, pal.skin), roughness: 0.66, metalness: 0.0,
    }),
    dark: new THREE.MeshStandardMaterial({
      map: rubberTex('shared', '#3c3e41'), roughness: 0.62, metalness: 0.04,
    }),
    metal: new THREE.MeshStandardMaterial({ color: 0x4c5057, roughness: 0.38, metalness: 0.85 }),
    /*
     * Sclera. Its own material because nothing else on a soldier is this
     * bright, and the contrast between it and the iris is the entire reason an
     * eye reads as an eye rather than a bead — which is what a single dark
     * sphere in a socket was.
     *
     * Deliberately NOT white: this is a mid warm grey, because a real sclera
     * sits in shadow under a brow and is never a light source. Pure white here
     * blooms under ACES and turns the face into a pair of headlamps. Roughness
     * 0.30 is what gives the wet specular catch.
     */
    eye: new THREE.MeshStandardMaterial({ color: 0x9a948a, roughness: 0.32, metalness: 0.0 }),
  };

  const skeleton = new THREE.Skeleton(list);
  const geos = buildSoldierGeometry(lo);
  const meshes = [];
  for (const k of Object.keys(geos)) {
    if (!geos[k]) continue;
    const sm = new THREE.SkinnedMesh(geos[k], mats[k]);
    sm.castShadow = true;
    sm.receiveShadow = true;
    sm.frustumCulled = false;
    rig.add(sm);
    sm.bind(skeleton);
    meshes.push(sm);
  }

  // The weapon hangs off the chest, not off a hand. Both arms are then IK'd
  // onto its grips, which is the only way to guarantee the stock stays
  // shouldered and both hands stay welded to the gun in every pose.
  const mount = new THREE.Object3D();
  map.chest.add(mount);

  const rifleGeo = buildRifle();
  const rifle = new THREE.Group();
  const rd = new THREE.Mesh(rifleGeo.dark, mats.dark);
  const rm = new THREE.Mesh(rifleGeo.metal, mats.metal);
  rd.castShadow = rm.castShadow = true;
  rifle.add(rd, rm);
  mount.add(rifle);

  const muzzle = new THREE.Object3D();
  muzzle.position.set(0, 0.006, 0.53);
  rifle.add(muzzle);

  // Grip sockets are wrist targets, so they sit above their grips.
  const gripTrigger = new THREE.Object3D();
  gripTrigger.position.set(0, -0.045, -0.055);
  rifle.add(gripTrigger);
  const gripSupport = new THREE.Object3D();
  gripSupport.position.set(0, -0.020, 0.275);
  rifle.add(gripSupport);
  const magWell = new THREE.Object3D();
  magWell.position.set(0, -0.175, 0.085);
  rifle.add(magWell);

  g.scale.setScalar(lo.heightScale);

  return {
    root: g, rig, bones: map, boneList: list, meshes, mats,
    mount, rifle, muzzle, gripTrigger, gripSupport, magWell, skeleton,
  };
}

/* ============================================================ animation ==== */

const POSE_KEYS = [
  'hipsY', 'hipsZoff', 'hipsRX', 'hipsRY', 'hipsRZ',
  'spineRX', 'spineRY', 'spineRZ',
  'chestRX', 'chestRY', 'chestRZ',
  'neckRX', 'neckRY', 'neckRZ',
  'clavLZ', 'shLX', 'shLY', 'shLZ', 'elLX', 'elLY', 'wrLX', 'wrLZ',
  'clavRZ', 'shRX', 'shRY', 'shRZ', 'elRX', 'elRY', 'wrRX', 'wrRZ',
  'hipLX', 'hipLY', 'hipLZ', 'knLX', 'anLX', 'anLZ',
  'hipRX', 'hipRY', 'hipRZ', 'knRX', 'anRX', 'anRZ',
];
function newPose() {
  const p = {};
  for (const k of POSE_KEYS) p[k] = 0;
  return p;
}
function zeroPose(p) { for (const k of POSE_KEYS) p[k] = 0; return p; }
function addPose(dst, src, w) { for (const k of POSE_KEYS) dst[k] += src[k] * w; return dst; }

const _scratchPose = newPose();
const _accum = newPose();

/* ------------------------------------------------------------- clips ------ */

/**
 * Every locomotion clip is phase-driven: `ph` advances with distance travelled,
 * not with wall time, so feet plant instead of skating.
 */
function clipIdle(o, t, breath) {
  zeroPose(o);
  const b = Math.sin(t * 1.5) * breath;
  o.hipsY = b * 0.006;
  o.spineRX = -0.04 + b * 0.012;
  o.chestRX = 0.03 + b * 0.02;
  o.neckRX = 0.02;
  o.hipLX = -0.02; o.hipRX = 0.02;
  o.knLX = 0.06; o.knRX = 0.10;
  // Negative hipLZ / positive hipRZ splay the legs *outward*: the sign here is
  // inverted relative to the X channels, and having it backwards was what made
  // the stance read knock-kneed with the boots touching.
  o.hipLZ = -0.028; o.hipRZ = 0.034;
  o.shLZ = 0.13; o.shRZ = -0.13;
  o.elLX = -0.24; o.elRX = -0.28;
  o.hipsRY = 0.03 + Math.sin(t * 0.42) * 0.03;
  o.chestRY = -0.03;
  return o;
}

function clipWalk(o, ph, k = 1) {
  zeroPose(o);
  const s = Math.sin(ph), c = Math.cos(ph);
  // Legs: swing + knee flexion timed to the swing phase.
  o.hipLX = s * 0.52 * k;
  o.hipRX = -s * 0.52 * k;
  o.knLX = (Math.max(0, -s) * 0.85 + Math.max(0, -c) * 0.25) * k;
  o.knRX = (Math.max(0, s) * 0.85 + Math.max(0, c) * 0.25) * k;
  o.anLX = (-s * 0.28 + 0.06) * k;
  o.anRX = (s * 0.28 + 0.06) * k;
  o.hipLZ = -0.022 * k; o.hipRZ = 0.022 * k;
  // Pelvis: two bobs per cycle, roll onto the stance leg, counter-yaw.
  o.hipsY = (-Math.abs(c) * 0.028 + 0.012) * k;
  o.hipsRY = -c * 0.11 * k;
  o.hipsRZ = s * 0.05 * k;
  o.hipsRX = 0.02 * k;
  // Shoulders counter-rotate against the pelvis.
  o.spineRY = c * 0.07 * k;
  o.chestRY = c * 0.10 * k;
  o.chestRX = 0.04 * k;
  o.neckRY = -c * 0.05 * k;
  // Arms swing opposite the legs.
  o.shLX = -s * 0.42 * k;
  o.shRX = s * 0.42 * k;
  o.shLZ = 0.14; o.shRZ = -0.14;
  o.elLX = (-0.30 - Math.max(0, -s) * 0.35) * k;
  o.elRX = (-0.30 - Math.max(0, s) * 0.35) * k;
  return o;
}

function clipRun(o, ph) {
  zeroPose(o);
  const s = Math.sin(ph), c = Math.cos(ph);
  o.hipLX = s * 0.85;
  o.hipRX = -s * 0.85;
  o.knLX = Math.max(0, -s) * 1.35 + 0.20 + Math.max(0, -c) * 0.35;
  o.knRX = Math.max(0, s) * 1.35 + 0.20 + Math.max(0, c) * 0.35;
  o.anLX = -s * 0.34 + 0.10;
  o.anRX = s * 0.34 + 0.10;
  o.hipsY = -Math.abs(c) * 0.05 + 0.02;
  o.hipsRY = -c * 0.17;
  o.hipsRZ = s * 0.07;
  o.hipsRX = 0.10;
  o.spineRX = 0.08;
  o.spineRY = c * 0.12;
  o.chestRY = c * 0.16;
  o.chestRX = 0.06;
  o.neckRX = -0.16;
  o.neckRY = -c * 0.08;
  o.shLX = -s * 0.72;
  o.shRX = s * 0.72;
  o.shLZ = 0.18; o.shRZ = -0.18;
  o.elLX = -0.75 - Math.max(0, -s) * 0.55;
  o.elRX = -0.75 - Math.max(0, s) * 0.55;
  return o;
}

function clipSprint(o, ph) {
  clipRun(o, ph);
  o.hipsRX = 0.20;
  o.spineRX = 0.14;
  o.chestRX = 0.10;
  o.neckRX = -0.30;
  o.hipLX *= 1.14; o.hipRX *= 1.14;
  o.shLX = -Math.sin(ph) * 1.0;
  o.shRX = Math.sin(ph) * 0.85;
  o.elLX = -1.55 - Math.max(0, -Math.sin(ph)) * 0.4;
  o.elRX = -1.35 - Math.max(0, Math.sin(ph)) * 0.4;
  o.shLZ = 0.30; o.shRZ = -0.26;
  return o;
}

/**
 * Crouch. The pelvis height is owned by `_applyPose` (-0.30 on crouchBlend);
 * this clip only supplies the leg fold that gets the boots to the floor at that
 * height. The two used to stack — a -0.26 here on top of the -0.30 there put
 * the hips 0.42 m off the deck, a full squat, and the foot IK then had to
 * mangle the legs to reach the ground.
 */
function clipCrouchWalk(o, ph, k) {
  zeroPose(o);
  const s = Math.sin(ph), c = Math.cos(ph);
  o.hipsY = -Math.abs(c) * 0.015 * k;
  o.hipsRX = 0.16;
  o.hipLX = 0.85 + s * 0.30 * k;
  o.hipRX = 0.85 - s * 0.30 * k;
  o.knLX = 1.70 + Math.max(0, -s) * 0.38 * k;
  o.knRX = 1.70 + Math.max(0, s) * 0.38 * k;
  o.anLX = -0.34; o.anRX = -0.34;
  o.hipLZ = -0.075; o.hipRZ = 0.075;
  o.spineRX = -0.10;
  o.chestRX = 0.04;
  o.hipsRY = -c * 0.05 * k;
  o.chestRY = c * 0.05 * k;
  return o;
}

function clipStrafe(o, ph, sign) {
  zeroPose(o);
  const s = Math.sin(ph) * sign;
  const lead = sign > 0 ? 'L' : 'R';
  const trail = sign > 0 ? 'R' : 'L';
  o[`hip${lead}Z`] = 0.30 * sign + s * 0.16;
  o[`hip${trail}Z`] = 0.05 * sign - s * 0.16;
  o[`hip${lead}X`] = s * 0.16;
  o[`hip${trail}X`] = -s * 0.16;
  o[`kn${lead}X`] = 0.22 + Math.max(0, s) * 0.55;
  o[`kn${trail}X`] = 0.22 + Math.max(0, -s) * 0.55;
  o.hipsY = -Math.abs(Math.cos(ph)) * 0.024;
  o.hipsRZ = -0.05 * sign;
  o.hipsRY = 0.14 * sign;
  o.spineRY = -0.09 * sign;
  o.chestRY = -0.10 * sign;
  o.anLX = 0.04; o.anRX = 0.04;
  o.shLZ = 0.14; o.shRZ = -0.14;
  return o;
}

/**
 * Turn-in-place is a pivot on the balls of the feet with a short shuffle step,
 * not a march. The hip swing and knee flexion are both kept small and in
 * phase: a rearward hip swing combined with heavy knee flexion shortens the
 * leg by 20 cm, which used to leave one boot hanging in mid air.
 */
function clipTurn(o, ph, sign) {
  zeroPose(o);
  const s = Math.sin(ph);
  o.hipLX = s * 0.13 * sign;
  o.hipRX = -s * 0.13 * sign;
  o.hipLZ = -0.03; o.hipRZ = 0.035;
  o.knLX = 0.13 + Math.max(0, -s * sign) * 0.16;
  o.knRX = 0.13 + Math.max(0, s * sign) * 0.16;
  o.anLX = -s * 0.09 * sign;
  o.anRX = s * 0.09 * sign;
  o.hipsRY = 0.11 * sign;
  o.hipsY = -0.010;
  o.shLZ = 0.14; o.shRZ = -0.14;
  return o;
}

/* ---------------------------------------------------------- weapon rig --- */

/**
 * Weapon mount poses, expressed in chest-bone space. The rig's +X is the
 * soldier's right, so the trigger arm is the `L`-suffixed bone chain.
 * `p` = position, `r` = euler. Everything else (hands, stock weld) falls out
 * of the two-bone IK, so these are the only numbers that need authoring.
 */
const WEAPON_POSES = {
  // Sighted: optic on the eye line, stock in the shoulder pocket.
  ads: { p: [-0.072, 0.278, 0.100], r: [0.00, 0.03, -0.06] },
  // Alert but not shooting: muzzle a hand's width below the target.
  ready: { p: [-0.098, 0.150, 0.090], r: [-0.42, 0.14, -0.12] },
  // Unaware patrol carry, muzzle well down.
  patrol: { p: [-0.110, 0.010, 0.060], r: [-0.72, 0.26, -0.18] },
  // Sprint: rifle diagonal across the chest, muzzle down and inboard.
  sprint: { p: [-0.060, -0.050, 0.075], r: [-0.45, 0.85, -0.60] },
  // Reload: rifle canted inboard so the mag well is reachable.
  reload: { p: [-0.075, 0.090, 0.115], r: [-0.30, 0.52, -0.36] },
};

const _mp = new THREE.Vector3();
const _mr = new THREE.Euler();
const _tgtA = new THREE.Vector3();
const _tgtB = new THREE.Vector3();
const _poleA = new THREE.Vector3();
const _poleB = new THREE.Vector3();
const _footT = new THREE.Vector3();
const _footPole = new THREE.Vector3();
const _qRig = new THREE.Quaternion();
const _qFoot = new THREE.Quaternion();
const DOWN = new THREE.Vector3(0, -1, 0);

/** Analytic two-bone IK. `a`->`b`->`c` with `c` landing on `target`. */
function ikArm(a, b, c, target, pole, L1, L2, twist) {
  a.parent.updateWorldMatrix(true, false);
  a.updateWorldMatrix(false, false);
  const pa = _v.setFromMatrixPosition(a.matrixWorld);
  _v2.subVectors(target, pa);
  let d = _v2.length();
  if (d < 1e-5) return;
  const maxD = (L1 + L2) * 0.999;
  const minD = Math.abs(L1 - L2) + 1e-3;
  d = clamp(d, minD, maxD);
  _v2.normalize();

  // Elbow position from the cosine rule, disambiguated by the pole vector.
  const ax = (L1 * L1 - L2 * L2 + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, L1 * L1 - ax * ax));
  _v3.copy(pole).addScaledVector(_v2, -pole.dot(_v2));
  if (_v3.lengthSq() < 1e-8) _v3.set(0, 0, 1).addScaledVector(_v2, -_v2.z);
  _v3.normalize();
  const elbow = _v3.multiplyScalar(h).addScaledVector(_v2, ax).add(pa);

  // Aim the upper arm at the elbow.
  _v2.subVectors(elbow, pa).normalize();
  _q.setFromRotationMatrix(a.parent.matrixWorld).invert();
  _v2.applyQuaternion(_q);
  a.quaternion.setFromUnitVectors(DOWN, _v2);
  if (twist) { _q2.setFromAxisAngle(DOWN, twist); a.quaternion.multiply(_q2); }
  // `true` for children matters: without it `b.matrixWorld` below is still the
  // previous frame's, so the second aim is solved from a stale joint position
  // and the end effector lands short of the target — which is why planted
  // boots used to sink 3-4 cm into the floor.
  a.updateWorldMatrix(false, true);

  // Aim the forearm at the target.
  const pb = _v.setFromMatrixPosition(b.matrixWorld);
  _v2.subVectors(target, pb).normalize();
  _q.setFromRotationMatrix(a.matrixWorld).invert();
  _v2.applyQuaternion(_q);
  b.quaternion.setFromUnitVectors(DOWN, _v2);
  if (c) c.quaternion.identity();
  b.updateWorldMatrix(false, true);
}

/* ============================================================== ragdoll ==== */

/** Node layout for the Verlet ragdoll. Positions are model-space rest. */
const RD = [
  ['head', 0, 1.64, 0, 0.13],
  ['chest', 0, 1.30, 0, 0.17],
  ['hips', 0, 0.98, 0, 0.15],
  ['elbowL', 0.19, 1.13, 0, 0.08],
  ['handL', 0.19, 0.88, 0, 0.07],
  ['elbowR', -0.19, 1.13, 0, 0.08],
  ['handR', -0.19, 0.88, 0, 0.07],
  ['kneeL', 0.105, 0.50, 0, 0.10],
  ['footL', 0.105, 0.09, 0.04, 0.10],
  ['kneeR', -0.105, 0.50, 0, 0.10],
  ['footR', -0.105, 0.09, 0.04, 0.10],
];
const RDI = {};
RD.forEach((e, i) => { RDI[e[0]] = i; });

const RD_LINKS = [
  ['head', 'chest', 1], ['chest', 'hips', 1],
  ['chest', 'elbowL', 1], ['elbowL', 'handL', 1],
  ['chest', 'elbowR', 1], ['elbowR', 'handR', 1],
  ['hips', 'kneeL', 1], ['kneeL', 'footL', 1],
  ['hips', 'kneeR', 1], ['kneeR', 'footR', 1],
  // Stabilisers keep the body from folding into itself.
  ['head', 'hips', 0.70], ['kneeL', 'kneeR', 0.2],
  ['chest', 'kneeL', 0.25], ['chest', 'kneeR', 0.25],
  ['elbowL', 'elbowR', 0.12], ['head', 'elbowL', 0.1], ['head', 'elbowR', 0.1],
];

/**
 * One-sided joint limits. Without these the chain has no notion of a knee or
 * an elbow and the corpse folds into a ball on impact.
 * [a, b, fraction of the rest distance below which the pair is pushed apart]
 */
const RD_LIMITS = [
  ['hips', 'footL', 0.74], ['hips', 'footR', 0.74],
  ['head', 'footL', 0.70], ['head', 'footR', 0.70],
  ['head', 'kneeL', 0.62], ['head', 'kneeR', 0.62],
  ['chest', 'handL', 0.52], ['chest', 'handR', 0.52],
  ['hips', 'handL', 0.48], ['hips', 'handR', 0.48],
  ['hips', 'elbowL', 0.55], ['hips', 'elbowR', 0.55],
  ['footL', 'footR', 0.5], ['head', 'hips', 0.86],
  ['chest', 'kneeL', 0.70], ['chest', 'kneeR', 0.70],
  ['chest', 'footL', 0.62], ['chest', 'footR', 0.62],
  ['elbowL', 'kneeL', 0.55], ['elbowR', 'kneeR', 0.55],
];

class Ragdoll {
  constructor(enemy, impulse, hitPoint, hitRegion) {
    this.enemy = enemy;
    const root = enemy.root;
    root.updateMatrixWorld(true);
    const scale = enemy.loadout.heightScale;
    this.p = [];
    this.prev = [];
    this.r = [];
    this.floor = [];
    for (const [, x, y, z, r] of RD) {
      // Model space is the flipped rig space, hence the extra half turn.
      const wp = new THREE.Vector3(x, y, z).multiplyScalar(scale)
        .applyAxisAngle(_up, enemy.yaw + Math.PI).add(root.position);
      this.p.push(wp);
      this.prev.push(wp.clone());
      this.r.push(r * scale);
      this.floor.push(-Infinity);
    }
    this.links = RD_LINKS.map(([a, b, stiff]) => ({
      a: RDI[a], b: RDI[b], len: this.p[RDI[a]].distanceTo(this.p[RDI[b]]), stiff,
    }));
    this.limits = RD_LIMITS.map(([a, b, frac]) => ({
      a: RDI[a], b: RDI[b], min: this.p[RDI[a]].distanceTo(this.p[RDI[b]]) * frac,
    }));

    // Seed velocity from the shot. Verlet stores velocity as a position
    // delta over one substep, so metres-per-second must be scaled by H.
    const H = 1 / 90;
    const imp = impulse.clone();
    for (let i = 0; i < this.p.length; i++) {
      const d = hitPoint ? this.p[i].distanceTo(hitPoint) : 1;
      const local = hitPoint ? clamp(1 - d / 0.7, 0, 1) : 0;
      this.prev[i].addScaledVector(imp, -H * (0.25 + local * 0.9));
    }
    if (hitRegion === 'head') this.prev[RDI.head].addScaledVector(imp, -H * 1.4);
    // A little angular kick so they don't all fall the same way.
    const side = (Math.random() - 0.5) * 2;
    _v.set(-imp.z, 0, imp.x).normalize().multiplyScalar(H * 2.2 * side);
    this.prev[RDI.head].sub(_v);
    this.prev[RDI.hips].add(_v);
    // Legs buckle: they keep almost none of the body's momentum.
    for (const n of ['kneeL', 'kneeR', 'footL', 'footR']) {
      this.prev[RDI[n]].addScaledVector(imp, H * 0.18);
    }

    this.settled = false;
    this.settleTimer = 0;
    this.physics = enemy.world.physics;
    this.floorProbe = 0;
    this._probeFloors();
  }

  _probeFloors() {
    const down = _v2.set(0, -1, 0);
    for (let i = 0; i < this.p.length; i++) {
      _v3.copy(this.p[i]); _v3.y += 1.2;
      const hit = this.physics.raycast(_v3, down, 6);
      this.floor[i] = hit ? hit.point.y : this.enemy.root.position.y;
    }
  }

  step(dt) {
    if (this.settled) return;
    // A little under world gravity: corpses read heavier without slamming.
    const g = this.physics.gravity * 0.75;
    const drag = Math.exp(-1.4 * dt);
    let motion = 0;

    for (let i = 0; i < this.p.length; i++) {
      const p = this.p[i], pr = this.prev[i];
      _v.subVectors(p, pr).multiplyScalar(drag);
      if (_v.lengthSq() > 0.36) _v.setLength(0.6);
      pr.copy(p);
      p.add(_v);
      p.y -= g * dt * dt;
      motion += _v.lengthSq();
    }

    for (let it = 0; it < 8; it++) {
      for (const l of this.links) {
        const a = this.p[l.a], b = this.p[l.b];
        _v.subVectors(b, a);
        const d = _v.length();
        if (d < 1e-5) continue;
        const diff = ((d - l.len) / d) * 0.5 * l.stiff;
        a.addScaledVector(_v, diff);
        b.addScaledVector(_v, -diff);
      }
      // Joint limits: push apart only, so limbs cannot fold through themselves.
      for (const l of this.limits) {
        const a = this.p[l.a], b = this.p[l.b];
        _v.subVectors(b, a);
        const d = _v.length();
        if (d < 1e-5 || d >= l.min) continue;
        const diff = ((d - l.min) / d) * 0.5;
        a.addScaledVector(_v, diff);
        b.addScaledVector(_v, -diff);
      }
      // Ground + world.
      for (let i = 0; i < this.p.length; i++) {
        const p = this.p[i];
        const fl = this.floor[i] + this.r[i] * 0.78;
        if (p.y < fl) {
          p.y = fl;
          // Friction against the ground.
          const pr = this.prev[i];
          // Heavy ground friction: a body in kit does not slide, it drops.
          pr.x = lerp(pr.x, p.x, 0.80);
          pr.z = lerp(pr.z, p.z, 0.80);
        }
        const world = this.physics.world;
        if (world && world.sphereIntersect) {
          _sph.center.copy(p); _sph.radius = this.r[i];
          const res = world.sphereIntersect(_sph);
          if (res) p.addScaledVector(res.normal, res.depth);
        }
      }
    }

    this.floorProbe -= dt;
    if (this.floorProbe <= 0) { this.floorProbe = 0.35; this._probeFloors(); }

    if (motion < 1.5e-5) {
      this.settleTimer += dt;
      if (this.settleTimer > 0.35) this.settled = true;
    } else this.settleTimer = 0;
  }

  /** Drive the skeleton from the particle chain. */
  apply(parts, yaw, scale) {
    const root = parts.root;
    const b = parts.bones;
    // Root follows the pelvis; the limbs are then aimed at their particles, so
    // the root only needs to carry position and the original facing.
    root.rotation.set(0, yaw, 0);
    root.position.copy(this.p[RDI.hips]);
    root.position.y -= 0.98 * scale;
    root.updateMatrixWorld(true);

    const aim = (bone, target, restDir) => {
      bone.updateWorldMatrix(true, false);
      _v.setFromMatrixPosition(bone.matrixWorld);
      _v2.copy(target).sub(_v);
      if (_v2.lengthSq() < 1e-8) return;
      _v2.normalize();
      bone.parent.updateWorldMatrix(true, false);
      _q.setFromRotationMatrix(bone.parent.matrixWorld).invert();
      _v2.applyQuaternion(_q);
      bone.quaternion.setFromUnitVectors(restDir, _v2);
      bone.updateMatrixWorld(true);
    };
    const DOWN = _v3.set(0, -1, 0);
    const UP = new THREE.Vector3(0, 1, 0);

    b.hips.rotation.set(0, 0, 0);
    b.hips.updateMatrixWorld(true);
    aim(b.spine, this.p[RDI.chest], UP);
    aim(b.chest, this.p[RDI.head], UP);
    b.neck.rotation.set(0, 0, 0);
    b.head.rotation.set(0, 0, 0);
    b.neck.updateMatrixWorld(true);
    aim(b.shL, this.p[RDI.elbowL], DOWN.clone());
    aim(b.elL, this.p[RDI.handL], DOWN.clone());
    aim(b.shR, this.p[RDI.elbowR], DOWN.clone());
    aim(b.elR, this.p[RDI.handR], DOWN.clone());
    aim(b.hipL, this.p[RDI.kneeL], DOWN.clone());
    aim(b.knL, this.p[RDI.footL], DOWN.clone());
    aim(b.hipR, this.p[RDI.kneeR], DOWN.clone());
    aim(b.knR, this.p[RDI.footR], DOWN.clone());
    b.anL.rotation.set(0.35, 0, 0);
    b.anR.rotation.set(0.35, 0, 0);
    b.wrL.rotation.set(0, 0, 0);
    b.wrR.rotation.set(0, 0, 0);
  }
}

/* ================================================================ squad ==== */

/**
 * Extremely light squad blackboard. Enemies coordinate through it so they
 * don't all charge at once, and so two of them never claim the same barrier.
 */
/**
 * Per-team blackboard.
 *
 * This was one global singleton whose role assignment sorted everyone by
 * distance to the player, and which "heard" gunfire by watching the player's
 * magazine count. With two teams that is wrong twice over: red would assign
 * roles against the player while fighting blue, and bot gunfire made no sound
 * at all. Now each team has its own board and shots are pushed in explicitly
 * via `world.onShotFired(pos, team)`.
 */
const BOARDS = new Map();

function boardFor(team) {
  let b = BOARDS.get(team);
  if (!b) {
    b = {
      team,
      members: [],
      coverClaims: new Map(),   // coverIndex -> enemy id
      lastGunshot: null,
      lastGunshotTime: -100,
    };
    BOARDS.set(team, b);
  }
  return b;
}

// Shared clock/frame counter, plus back-compat aliases: several call sites
// (and the exported Enemy.SQUAD) still read SQUAD.time / .frame / .coverClaims.
const SQUAD = {
  time: 0,
  frame: 0,
  playerAmmo: null,
  get members() { return [...BOARDS.values()].flatMap((b) => b.members); },
  get coverClaims() { return boardFor('red').coverClaims; },
  get lastGunshot() { return boardFor('red').lastGunshot; },
  get lastGunshotTime() { return boardFor('red').lastGunshotTime; },
  boardFor,
};

function squadJoin(e) { boardFor(e.team).members.push(e); }
function squadLeave(e) {
  const b = boardFor(e.team);
  const i = b.members.indexOf(e);
  if (i >= 0) b.members.splice(i, 1);
  for (const [k, v] of b.coverClaims) if (v === e.id) b.coverClaims.delete(k);
}

/** Report a shot so the other team can react to it. */
function squadHearShot(pos, shooterTeam) {
  for (const b of BOARDS.values()) {
    if (b.team === shooterTeam) continue;
    b.lastGunshot = pos.clone ? pos.clone() : pos;
    b.lastGunshotTime = SQUAD.time;
  }
}

function squadTick(world, dt) {
  SQUAD.time += dt;
  SQUAD.frame++;

  // The player's own gunfire, still inferred from the magazine so no other file
  // has to cooperate. Bot shots arrive through squadHearShot instead.
  const w = world.player?.weapon ?? (typeof window !== 'undefined' ? window.__game?.game?.weapon : null);
  if (w && world.player) {
    if (SQUAD.playerAmmo !== null && w.ammo < SQUAD.playerAmmo) {
      squadHearShot(world.player.position, world.player.team ?? 'blue');
    }
    SQUAD.playerAmmo = w.ammo;
  }

  // Role assignment, per team, relative to that team's own focus rather than
  // always the player.
  for (const b of BOARDS.values()) {
    const live = b.members.filter((m) => m.alive && m.alert > 0.4);
    if (!live.length) continue;
    const focus = live.find((m) => m.actor)?.actor ?? world.player;
    if (!focus) continue;
    const pp = Enemy.aimPoint(focus, _sq);
    live.sort((a, c) => a.root.position.distanceToSquared(pp) - c.root.position.distanceToSquared(pp));
    for (let i = 0; i < live.length; i++) {
      const m = live[i];
      const want = i === 0 ? 'assault'
        : i === 1 ? (m._flankBias ? 'flank' : 'assault')
          : i < 4 ? 'flank' : 'suppress';
      if (m.role !== want && m.roleLock <= 0) {
        m.role = want;
        m.roleLock = 2.5 + Math.random() * 2.5;
      }
    }
  }
}

/* ================================================================ agent ==== */

export class Enemy {
  /**
   * @param {'red'|'blue'} team — 'red' keeps deathmatch behaviour unchanged.
   * @param {{ffa?:boolean, op?:number}} opts
   *   `ffa` decides which of the operator's two palettes it wears, and it has
   *   to be known HERE rather than stamped on afterwards: the model is built in
   *   this constructor, so a flag set on the next line arrives too late to
   *   colour anything. `op` pins a specific operator — used for a networked
   *   player, who picked one, rather than a bot, which is assigned one.
   */
  constructor(world, position, id, team = 'red', opts = {}) {
    this.world = world;
    this.id = id;
    this.ffa = !!opts.ffa;
    /**
     * Which of the operator's two palettes it wears. NOT `ffa`: online team
     * deathmatch is a free-for-all for targeting purposes and a TEAM game for
     * telling people apart, and dressing both sides in the same twelve colours
     * there was unplayable. `teamsMatter` is the flag that means "sides are
     * visible", so it is the one that picks the palette.
     */
    const solo = !world.teamsMatter;
    this.loadout = loadoutFor(id, solo ? null : team,
      { ffa: solo, operator: opts.operator ?? null });
    this.operator = this.loadout.operator;
    // Scoreboard identity. The board used to be a hard-coded fake roster.
    this.name = callsignFor(id);
    this.kills = 0;
    this.deaths = 0;
    this.parts = makeSoldier(this.loadout);
    this.root = this.parts.root;
    this.root.position.copy(position);
    world.scene.add(this.root);

    // Stands 1.96 m against the player's 1.72 m. Matching the player looks
    // more correct and measured worse (plants 25-30% -> 15% over 20 rounds),
    // so the mismatch stays until there is a reason beyond tidiness.
    this.capsule = new Capsule(
      position.clone().setY(position.y + 0.34),
      position.clone().setY(position.y + 1.62),
      0.34
    );
    this.velocity = new THREE.Vector3();
    this.grounded = false;

    this.health = 100;
    this.alive = true;
    this.isEnemy = true;          // lets aimPoint/hit tests tell bots from the player
    this.team = team;
    this.target = null;           // resolved by world.findTarget each retarget tick
    this._removed = false;
    this.state = 'idle';
    this.stateTime = 0;
    this.yaw = Math.random() * Math.PI * 2;
    this.targetYaw = this.yaw;
    this.aimYaw = this.yaw;          // upper-body aim direction
    this.aimPitch = 0;
    this.fireTimer = 0;
    this.burst = 0;
    this.alert = 0;
    this.lastKnown = new THREE.Vector3(position.x, position.y, position.z);
    this.destination = null;
    this.repathTimer = 0;
    this.strafeDir = Math.random() < 0.5 ? -1 : 1;
    this.hitFlash = 0;
    this.deathTimer = 0;
    this.ragdoll = null;

    // ---- locomotion / animation state
    this.gaitPhase = Math.random() * Math.PI * 2;
    this.animTime = Math.random() * 10;
    this.blend = { idle: 1, walk: 0, run: 0, sprint: 0, crouch: 0, strafeL: 0, strafeR: 0, turn: 0 };
    this.aimBlend = 0;
    this.adsBlend = 0;
    this.lowReady = 1;
    this.crouched = false;
    this.crouchBlend = 0;
    this.recoil = 0;
    this.recoilVel = 0;
    this.flinch = new THREE.Vector3();   // x pitch, y yaw, z roll additive
    this.flinchVel = new THREE.Vector3();
    this.hitRegion = null;
    this.reloading = 0;
    this.ammo = 30;
    this.mantle = 0;
    this.turnSign = 1;
    this.sprintBlend = 0;
    this.reloadBlend = 0;
    this.sprintFlag = false;
    // Fixed limb lengths for the arm IK (rest pose, scaled with the model).
    this.armUpper = 0.27 * this.loadout.heightScale;
    this.armLower = 0.25 * this.loadout.heightScale;

    // ---- combat tuning: CoD bot, not aimbot
    this.speeds = { walk: 1.9, run: 4.3, sprint: 5.6, crouch: 1.15 };
    this.baseAccuracy = 0.030 + Math.random() * 0.018;
    this.accuracy = this.baseAccuracy;
    this.reactionTime = 0.30 + Math.random() * 0.22;
    this.reaction = 0;
    /*
     * Difficulty is resolved per bot at spawn, off the same facade everything
     * else comes through. The per-bot roll above stays as the VARIATION — two
     * Veterans are still not the same shooter — and the preset multiplies it,
     * so the spread of skill within a squad survives every setting.
     */
    applySkill(this, world.difficulty?.() ?? DEFAULT_DIFFICULTY);
    this.firstBurst = true;
    this.role = 'suppress';
    this.roleLock = 0;
    this._flankBias = Math.random() < 0.5;
    this.cover = null;         // { index, pos, stand }
    this.peek = 0;             // 0 hidden, 1 exposed
    this.peekTimer = 1 + Math.random() * 2;
    /**
     * Seconds before this bot may claim cover again after abandoning a slot.
     *
     * Without it the give-up in `_cover` is a loop: `_engage` sends every
     * `suppress` and `flank` bot straight back to cover on the next frame, it
     * walks at a slot it cannot reach for another six seconds, and the fight
     * never happens. Measured on Dust II as 17559 frames in cover against 738
     * in engage — the bots had stopped fighting and were commuting.
     */
    this.coverCooldown = 0;
    this.suppressed = 0;
    this.searchPoints = [];
    this.searchIdle = 0;
    this.lookOffset = 0;

    squadJoin(this);
  }

  get eyePosition() {
    return _v2.copy(this.capsule.end).setY(this.capsule.end.y - 0.02 - this.crouchBlend * 0.42);
  }

  /* ------------------------------------------------------------- hits ---- */

  /**
   * Bone-driven hit volumes. The old version sampled a fixed stack of spheres
   * down the capsule axis, which meant a leg mid-swing — up to 0.4 m forward of
   * the axis at a run — simply could not be shot, and an arm never could. Each
   * volume is now a capsule between two live bones, so what you see is what you
   * hit at every frame of every animation.
   * `pad` widens a volume past the bone line to cover the geometry around it.
   */
  raycastHit(origin, dir, maxDist) {
    if (!this.alive) return null;
    const b = this.parts.bones;
    const s = this.loadout.heightScale;

    // Bone world positions are current: the rig is solved in _animate and the
    // renderer refreshes the graph every frame. Read the matrices directly to
    // stay allocation-free.
    const bp = (bone, out) => out.setFromMatrixPosition(bone.matrixWorld);

    let bestT = Infinity, bestPart = null;

    /** Ray vs. capsule(p0,p1,r). Returns the entry distance or -1. */
    const hitSeg = (p0, p1, r) => {
      _hitA.subVectors(p1, p0);
      _hitB.subVectors(p0, origin);
      const aa = _hitA.dot(_hitA);
      const ab = _hitA.dot(dir);
      const ac = _hitA.dot(_hitB);
      const bb = _hitB.dot(dir);
      const cc = _hitB.dot(_hitB);
      // Closest approach between the (infinite) ray and the segment.
      const den = aa - ab * ab;
      let tSeg;
      if (Math.abs(den) < 1e-8) tSeg = 0;
      else tSeg = clamp((ab * bb - ac) / den, 0, 1);
      // Closest point on the ray to that segment point.
      let tRay = ab * tSeg + bb;
      if (tRay < 0) tRay = 0;
      _hitC.copy(p0).addScaledVector(_hitA, tSeg);
      _hitD.copy(origin).addScaledVector(dir, tRay);
      const d2 = _hitC.distanceToSquared(_hitD);
      if (d2 > r * r) return -1;
      // Step back to the surface so the impact FX land on the outside.
      const t = Math.max(0, tRay - Math.sqrt(Math.max(0, r * r - d2)));
      void cc;
      return t <= maxDist ? t : -1;
    };

    const test = (b0, b1, r, part, lift0 = 0, lift1 = 0) => {
      bp(b0, _hitP0); bp(b1, _hitP1);
      _hitP0.y += lift0 * s; _hitP1.y += lift1 * s;
      const t = hitSeg(_hitP0, _hitP1, r * s);
      if (t >= 0 && t < bestT) { bestT = t; bestPart = part; }
    };

    // Head: the skull sits above the head bone, so lift the segment into it.
    // 0.142 matches the helmet's real half-width at the ear skirts (0.113 rail
    // line + 0.029 skirt); the lower lift closes a 4 cm band over the
    // trapezius that opened up in a deep crouch.
    test(b.head, b.head, 0.142, 'head', 0.040, 0.135);
    // Torso. The chest band stops at the chest bone rather than the neck: run
    // it up to the neck and the plate carrier's 0.21 m radius bulges further
    // forward than the skull does, so every headshot resolves as a body hit.
    // Torso, split into the zones the damage table prices separately.
    test(b.hips, b.spine, 0.185, 'stomach');
    test(b.spine, b.chest, 0.205, 'chest');
    test(b.chest, b.neck, 0.110, 'chest');
    // Shoulder yoke. Without it there is a live hole over each trapezius:
    // the neck volume is only 0.11 across and the upper-arm volume does not
    // start until 0.19 out, so rounds through the deltoid line passed through.
    test(b.chest, b.shL, 0.115, 'chest'); test(b.chest, b.shR, 0.115, 'chest');
    // Arms count as the chest they are hanging in front of.
    //
    // Not because an arm IS a chest, but because the server hit-tests against a
    // vertical spine and cannot tell them apart. Giving them their own price
    // here would mean the damage you saw and the damage that counted differed
    // exactly when someone shielded their body with an arm.
    test(b.shL, b.elL, 0.085, 'chest'); test(b.elL, b.hnL, 0.075, 'chest');
    test(b.shR, b.elR, 0.085, 'chest'); test(b.elR, b.hnR, 0.075, 'chest');
    // Legs + boots.
    test(b.hipL, b.knL, 0.115, 'legs'); test(b.knL, b.anL, 0.095, 'legs');
    test(b.hipR, b.knR, 0.115, 'legs'); test(b.knR, b.anR, 0.095, 'legs');
    test(b.anL, b.toL, 0.085, 'legs'); test(b.anR, b.toR, 0.085, 'legs');

    if (bestPart === null) return null;
    return { distance: bestT, part: bestPart, point: origin.clone().addScaledVector(dir, bestT) };
  }

  damage(amount, part, fromDir, attacker = null) {
    if (!this.alive) return false;
    // Reported BEFORE the subtraction, and clamped to what is left, so a
    // finishing shot is worth the health it actually took rather than the
    // weapon's full number. The single choke point for every source of damage
    // in the offline game, which is why the stats hook lives here.
    this.world.onDamage?.(attacker, this, Math.min(amount, this.health), part);
    this.health -= amount;
    this.hitFlash = 1;
    this.alert = 1;
    this.suppressed = Math.min(1, this.suppressed + 0.6);
    // Attribute the hit to whoever actually fired. This used to always copy
    // the player's position, so a bot shot by a teammate's enemy would turn
    // around and hunt the player instead.
    if (attacker) {
      Enemy.aimPoint(attacker, _v3);
      this.lastKnown.copy(_v3);
      if (this.isFoe(attacker)) this.target = attacker;
    } else if (this.actor) {
      Enemy.aimPoint(this.actor, _v3);
      this.lastKnown.copy(_v3);
    }
    if (this.state === 'idle' || this.state === 'patrol') this.setState('engage');
    if (this.health <= 0) {
      this.kill(fromDir, part, attacker);
      return true;
    }
    this._hitReact(part, fromDir);
    return false;
  }

  /** Region-specific flinch that reads at combat distance. */
  _hitReact(part, fromDir) {
    const d = (fromDir ?? _dir.set(0, 0, 1)).clone().setY(0).normalize();
    // Convert the incoming direction into the enemy's local frame.
    const fx = Math.sin(this.yaw), fz = Math.cos(this.yaw);
    const fwd = -d.x * -fx - d.z * -fz;          // +1 shot from behind
    const side = d.x * fz - d.z * fx;
    const mag = part === 'head' ? 1.6 : 1.0;
    this.flinchVel.x += fwd * 5.5 * mag;
    this.flinchVel.y += side * 3.2 * mag;
    this.flinchVel.z += -side * 4.0 * mag;
    this.hitRegion = part;
    // Being hit spoils aim for a moment.
    this.reaction = Math.max(this.reaction, 0.12);
    this.accuracy = this.baseAccuracy * 2.2;
  }

  kill(fromDir, part, killer = null) {
    if (!this.alive) return;
    this.killer = killer;
    this.alive = false;
    this.state = 'dead';
    this.deathTimer = 0;
    const d = (fromDir ?? new THREE.Vector3(0, 0, 1)).clone();
    if (d.lengthSq() < 1e-6) d.set(0, 0, 1);
    d.normalize();
    this.deathDir = d;
    _v.copy(this.root.position);
    _v.y += part === 'head' ? 1.63 : 1.25;
    this.ragdoll = new Ragdoll(this, d.clone().multiplyScalar(part === 'head' ? 2.3 : 1.7), _v.clone(), part);
    // The weapon slumps against the chest as the body goes limp.
    this.parts.mount.position.set(-0.10, -0.02, 0.05);
    this.parts.mount.rotation.set(-1.0, 0.5, -0.4);
    squadLeave(this);
    this.onDeath?.(this, this.killer ?? null);
  }

  setState(s) {
    // Anything that would drop an objective-holding bot into aimless patrol
    // sends it back to the objective instead.
    if (s === 'patrol' && this.objective) s = 'objective';
    // A bot mid-plant does not get pulled into a firefight by damage or noise.
    if (this.committed && (s === 'engage' || s === 'cover' || s === 'search')) return;

    if (this.state === s) return;
    this.state = s;
    this.stateTime = 0;
    if (s === 'engage') {
      this.reaction = this.reactionTime;
      this.firstBurst = true;
    }
    if (s === 'search') {
      this._planSearch();
    }
  }

  /* ------------------------------------------------------------ target -- */

  /**
   * Who this bot is currently fighting.
   *
   * Deathmatch has exactly one hostile — the player — and every reference in
   * here used to say so literally. Bomb mode needs red-vs-blue, so combat now
   * goes through this indirection: `world.findTarget(me)` picks the nearest
   * visible opponent (bot *or* player) and the AI state machine is otherwise
   * untouched. With no `findTarget` supplied it falls back to the player, so
   * deathmatch behaves exactly as before.
   */
  get actor() {
    return this.target ?? this.world.player;
  }

  /** True if `other` is on the opposing side. The player counts as an actor. */
  isFoe(other) {
    if (!other) return false;
    // Free-for-all: everyone is a foe except yourself. Without this, deathmatch
    // bots all carried team 'red' and the team comparison below made every bot
    // friendly to every other bot — so eight of them stood in the same map
    // hunting the player and nobody else, and the map never felt like a fight
    // that existed independently of you.
    if (this.ffa) return other !== this;
    const mine = this.team;
    const theirs = other.team ?? 'blue';   // the player defaults to blue
    return mine !== theirs;
  }

  /**
   * Chest-height aim point for any actor. The player's `position` is already
   * eye height (it is `capsule.end`); a bot's `root.position` is at its feet,
   * so it needs lifting or every bot shoots its opponent in the shins.
   */
  static aimPoint(actor, out) {
    if (!actor) return out.set(0, 0, 0);
    if (actor.isEnemy) return out.copy(actor.root.position).setY(actor.root.position.y + 1.35);
    return out.copy(actor.position);
  }

  /* --------------------------------------------------------- perception -- */

  /** @deprecated name kept so external callers keep working. */
  canSeePlayer() { return this.canSeeTarget(); }

  canSeeTarget() {
    const player = this.actor;
    if (!player) return false;
    const eye = _v3.copy(this.eyePosition);
    Enemy.aimPoint(player, _dir).sub(eye);
    const dist = _dir.length();
    const sk = this.skill;
    if (dist > Math.max(90, sk.maxRange + 15)) return false;
    _dir.divideScalar(dist);

    /*
     * Attention cone. This is the knob that decides whether a hard bot reads as
     * skilled or as cheating: one that reacts instantly to something behind it
     * is a wallhack whatever its accuracy says. A bot that has not looked your
     * way yet is fair game at every difficulty, so the cone tightens rather
     * than vanishing even at Elite.
     */
    const fx = -Math.sin(this.yaw), fz = -Math.cos(this.yaw);
    const cos = fx * _dir.x + fz * _dir.z;
    if (this.alert < 0.5 && cos < sk.fov.idle) return false;
    if (this.alert >= 0.5 && cos < sk.fov.alert) return false;

    /*
     * Both directions, and that is not belt-and-braces — it is the invariant.
     *
     * `Octree.rayIntersect` culls backfaces, so a ray leaving an embedded eye
     * sees nothing at all: the only triangle on its path is the far face of the
     * brush, from behind. One forward cast therefore reported CLEAR for a bot
     * standing inside a wall, while the player's rounds hit the wall's near
     * face and stopped — "бот в стене, невозможно убить, но он стреляет",
     * straight out of a playtest. The line of sight was asymmetric and the
     * asymmetry always favoured the bot.
     *
     * Casting back from the target closes it: that ray meets the same wall from
     * the outside, where it is a frontface, and blocks. "I can shoot you" and
     * "you can shoot me" become one predicate, which is what fairness means
     * here. Only paid when the forward cast is clear, so it costs nothing in
     * the common case of a bot that already cannot see anybody.
     */
    /*
     * Known to be standing inside geometry: no sight, no shot, no argument.
     *
     * The reverse cast below covers the deep case, but it excludes the 0.45 m
     * nearest the eye (so that both casts walk the same segment), which leaves
     * a bot buried LESS than 0.45 m deep still reporting clear — the wall face
     * it is behind falls inside that exclusion. `_integrate` already computes
     * `_embedded` at 6 Hz for the recovery, so the shallow case costs a flag
     * read rather than another raycast.
     */
    if (this._embedded === undefined) {
      // Resolved lazily rather than waiting for `_integrate`'s 6 Hz sampler, so
      // the invariant holds on a bot's very first frame too. Once per bot life.
      this._embedded = this.world.physics.insideSolid(this.capsule.start, 1.2);
    }
    if (this._embedded) return false;

    const reach = dist - 0.45;
    if (this.world.physics.raycast(eye, _dir, reach)) return false;
    /*
     * The reverse ray must test the SAME SEGMENT, not the same length.
     *
     * The first version started at the target and ran `dist - 0.45`, which
     * covers [0.45, dist] from the eye — sliding the excluded 0.45 m from the
     * target end to the eye end. That 0.45 m exists to ignore the cover the
     * TARGET is stood against, so moving it made bots blind to anyone hugging a
     * corner: `round-probe` caught it immediately as rounds running out the
     * clock instead of ending in eliminations.
     *
     * Starting 0.45 m short of the target and running `dist - 0.9` walks
     * exactly the interval the forward cast walked, in the other direction —
     * which is the whole and only point.
     */
    if (reach <= 0.45) return true;
    _v.copy(_dir).negate();
    _hitA.copy(eye).addScaledVector(_dir, reach);
    return !this.world.physics.raycast(_hitA, _v, reach - 0.45);
  }

  /* ------------------------------------------------------------- update -- */

  update(dt) {
    if (SQUAD.frame !== this._sqFrame) {
      // First enemy to tick each frame advances the shared blackboard.
      if (!Enemy._tickedThisFrame) {
        Enemy._tickedThisFrame = true;
        squadTick(this.world, dt);
        queueMicrotask(() => { Enemy._tickedThisFrame = false; });
      }
      this._sqFrame = SQUAD.frame;
    }

    this.stateTime += dt;
    this.animTime += dt;
    this.hitFlash = Math.max(0, this.hitFlash - dt * 4);
    this.roleLock -= dt;
    this.coverCooldown -= dt;
    this.suppressed = Math.max(0, this.suppressed - dt * 0.5);
    this.accuracy = damp(this.accuracy, this.baseAccuracy, 1.5, dt);

    if (!this.alive) { this._updateDeath(dt); return; }

    const player = this.actor;
    const sees = this.canSeeTarget();
    if (sees) {
      this.alert = Math.min(1, this.alert + dt * 3.0 * this.skill.alertGain);
      this.lastKnown.copy(Enemy.aimPoint(player, _v3));
      this.lastSeen = SQUAD.time;
    } else {
      this.alert = Math.max(0, this.alert - dt * 0.2);
    }

    // Hearing: recent player gunfire within earshot alerts and cues a search.
    const board = boardFor(this.team);
    if (board.lastGunshot && SQUAD.time - board.lastGunshotTime < 0.4) {
      const ear = this.skill.hearing;
      const d = this.root.position.distanceTo(board.lastGunshot);
      if (d < ear) {
        this.alert = Math.min(1, this.alert + (1 - d / ear) * 0.55);
        this.lastKnown.copy(board.lastGunshot);
        if (this.state === 'idle' || this.state === 'patrol') this.setState('search');
      }
    }

    // Each state re-asserts its stance every frame. `forceCrouch` is the one
    // authoring hook: the capture harness needs to photograph a crouch without
    // having to manoeuvre the AI into a cover slot first.
    this.crouched = this.forceCrouch === true;

    switch (this.state) {
      case 'idle':
        this._idle(dt);
        break;
      case 'patrol':
        this._patrol(dt);
        break;
      case 'engage':
        this._engage(dt, sees);
        break;
      case 'cover':
        this._cover(dt, sees);
        break;
      case 'objective':
        this._objective(dt, sees);
        break;
      case 'search':
        this._search(dt, sees);
        break;
    }

    this._integrate(dt);
    this._animate(dt);
  }

  _idle(dt) {
    if (this.alert > 0.35) { this.setState('engage'); return; }
    // Idle scan: sweep the head, occasional turn-in-place.
    this.lookOffset = Math.sin(this.animTime * 0.35 + this.id) * 0.6;
    /*
     * Always leave. The old line re-rolled `idle` 40% of the time, so a bot
     * could sit through several 2.5-6.5 s windows in a row — measured at 9-21%
     * of all samples spent idle across the squad, which is the rest of "на
     * месте стоят". Scanning for a couple of seconds before moving off is the
     * behaviour worth keeping; standing there indefinitely is not.
     */
    if (this.stateTime > 1.6 + (this.id % 3) * 0.7) {
      this.targetYaw = this.yaw + (Math.random() - 0.5) * 2.4;
      this.setState('patrol');
    }
    this.velocity.x *= Math.exp(-9 * dt);
    this.velocity.z *= Math.exp(-9 * dt);
  }

  /**
   * Patrol: actually cross the map.
   *
   * What this used to be: pick a random authored spawn point, jitter it 10 m,
   * walk at it in a straight line with 2.6 m whiskers, and roll a 30% chance of
   * standing still at every waypoint. A playtester described the result exactly
   * — "на месте стоят, в стены тыкаются" — and both halves were authored:
   *
   *   - the dice roll meant roughly a third of the squad was idle at any
   *     moment, which from the outside is a bot that does not work;
   *   - straight-line steering cannot leave a room whose door is not on the
   *     bearing of the goal, so the rest leaned on walls.
   *
   * Routes come off the shared `NavGrid` now, which already existed and was
   * only being used by bomb defusal. Destinations come off the walkable grid
   * rather than the spawn list, and are biased FAR, because a patrol that picks
   * the nearest point never leaves its own corner.
   */
  _patrol(dt) {
    if (this.alert > 0.35) { this.setState('engage'); return; }

    const arrived = this.destination
      && this.root.position.distanceTo(this.destination) < 2.0;
    if (!this.destination || arrived) {
      // A short pause on arrival reads as a sweep and gives the squad time to
      // spread. Bounded and rare, unlike the old 30% coin flip.
      if (arrived && this.patrolHold == null && Math.random() < 0.35) {
        this.patrolHold = 0.8 + Math.random() * 1.4;
      }
      if (this.patrolHold != null) {
        this.patrolHold -= dt;
        this.lookOffset = Math.sin(this.animTime * 0.9) * 0.8;
        this.velocity.x *= Math.exp(-8 * dt);
        this.velocity.z *= Math.exp(-8 * dt);
        if (this.patrolHold > 0) return;
        this.patrolHold = null;
      }
      this.destination = this._patrolPoint();
      this.needsRepath = true;
    }
    this._navToward(this.destination, this.speeds.walk, dt, 2.0);
  }

  /**
   * Somewhere worth walking to: the best of a few walkable candidates, scored
   * on distance so the bot commits to crossing ground rather than shuffling.
   * Falls back to the authored spawn list when the level has no grid.
   */
  _patrolPoint() {
    const pts = this.world.navPoints?.() ?? null;
    if (!pts?.length) {
      const sp = this.world.level.spawnPoints ?? [];
      const q = sp.length
        ? sp[(Math.random() * sp.length) | 0].clone()
        : this.root.position.clone();
      q.x += (Math.random() - 0.5) * 10;
      q.z += (Math.random() - 0.5) * 10;
      return q;
    }
    let best = null, bestScore = -Infinity;
    for (let i = 0; i < 6; i++) {
      const c = pts[(Math.random() * pts.length) | 0];
      const d = this.root.position.distanceTo(c);
      // Not the furthest cell on the map — that is always the same corner and
      // the whole squad converges on it. Peak interest around 30 m.
      const score = d < 6 ? -1 : -Math.abs(d - 30) + Math.random() * 8;
      if (score > bestScore) { bestScore = score; best = c; }
    }
    return (best ?? this.root.position).clone();
  }

  /**
   * Plan a route to `goal` off the shared walkable grid.
   *
   * Rate-limited to 2 Hz per bot. A wedged bot asks for a repath every frame
   * otherwise, and a flow-field search per bot per frame is exactly the kind of
   * cost `perf-probe` exists to catch.
   */
  _planRoute(goal) {
    this.needsRepath = false;
    const now = SQUAD.time;
    if (this._repathAt != null && now - this._repathAt < 0.5) return;
    this._repathAt = now;
    const nav = this.world.nav?.();
    this.route = nav ? nav.route(this.root.position, goal) : null;
    this.routeIdx = 0;
    this.routeStuck = 0;
    this._lastProg = null;
    (this._routeGoal ??= new THREE.Vector3()).copy(goal);
  }

  /**
   * Walk to `goal` along a planned route, with the wedge watchdog.
   *
   * Used by patrol and search. `_objective` keeps its own copy of this loop on
   * purpose: its routes are handed in by the game mode, not planned here, and
   * that path is measured against the bomb plant rate — one shared function
   * would have to serve both and the merge is not worth the risk to a number
   * that already works.
   *
   * @returns true once inside `arrive` of the goal
   */
  _navToward(goal, speed, dt, arrive = 1.6) {
    const here = this.root.position;
    if (here.distanceTo(goal) <= arrive) return true;

    const stale = this.needsRepath
      || !this.route
      || this.routeIdx >= this.route.length
      || !this._routeGoal
      || this._routeGoal.distanceToSquared(goal) > 9;
    if (stale) this._planRoute(goal);

    let wp = goal;
    let onRoute = false;
    if (this.route && this.routeIdx < this.route.length) {
      wp = this.route[this.routeIdx];
      if (here.distanceTo(wp) < 2.2) {
        this.routeIdx++;
        this._lastProg = null;
        this.routeStuck = 0;
        wp = this.routeIdx < this.route.length ? this.route[this.routeIdx] : goal;
      }
      onRoute = this.routeIdx < this.route.length;
    }

    // Progress watchdog. Skipping to the next waypoint is the obvious recovery
    // and the wrong one — it aims the bot further into whatever it is stuck on.
    // Reverse out, then plan again from where it actually is.
    const prog = here.distanceToSquared(wp);
    if (this._lastProg == null || prog < this._lastProg - 0.25) {
      this._lastProg = prog;
      this.routeStuck = 0;
    } else {
      this.routeStuck = (this.routeStuck ?? 0) + dt;
      if (this.routeStuck > 1.6) {
        this.needsRepath = true;
        this.unwedge = 0.7;
        this.routeStuck = 0;
        this._lastProg = null;
      }
    }

    if (this.unwedge > 0) {
      this.unwedge -= dt;
      // Straight back the way we came, ignoring whiskers — they are what got
      // us in here.
      _dir.subVectors(here, wp).setY(0).normalize();
      this.velocity.addScaledVector(_dir, Math.min(24 * dt, this.speeds.walk));
      return false;
    }

    // Short whiskers on a planned route: the corridor is already known to be
    // walkable, so long probes only turn the bot out of the passage it is
    // trying to enter.
    this._moveToward(wp, speed, dt, onRoute ? 1.3 : 2.6);
    return false;
  }

  /* --------------------------------------------------------- objective --- */

  /**
   * Push toward, or hold, an objective.
   *
   * The mode assigns `this.objective = { pos, kind, radius }` and this walks
   * there via `this.route` — a short hand-authored waypoint chain — then hands
   * back to normal combat once a foe shows up. There is deliberately NO
   * pathfinding here: `_steer` is reactive whisker avoidance with 2.6 m probes,
   * which wedges bots in Dust II's doorways if you point them at a distant goal
   * and hope. Waypoints turn that into an authoring problem instead.
   *
   * Combat still wins: if the bot can see an enemy it fights, because a bot
   * that walks past a firefight to touch a waypoint looks broken.
   */
  _objective(dt, sees) {
    const obj = this.objective;
    if (!obj) { this.setState('patrol'); return; }

    /*
     * Committed = standing in the plant/defuse zone with a job to finish.
     *
     * A committed bot must not break off to shoot. A contested site always has
     * a defender in view, so deferring to combat there means the timer never
     * accumulates and the bomb never goes down — the sim sat at zero plants on
     * exactly this. Planting under fire is also what CS bots do.
     */
    const arrived = this.root.position.distanceTo(obj.pos) <= (obj.radius ?? 2.5);
    this.committed = arrived && (obj.kind === 'plant' || obj.kind === 'defuse');

    // Otherwise seeing a foe outranks the objective — but only out to a sane
    // distance, so a bot does not abandon a plant to chase across the map.
    if (!this.committed && sees && this.actor) {
      // The bomb carrier is not a rifleman: it stops only for threats close
      // enough to be about to kill it, and lets the escorts trade at range.
      // That is what the escorts are for, and standing to fight at 40 m is how
      // a round runs out of clock with the bomb still in a pocket.
      const carrying = obj.kind === 'plant' || obj.kind === 'retrieve';
      const d = this.root.position.distanceTo(Enemy.aimPoint(this.actor, _v3));
      if (d < (carrying ? 16 : 45)) { this.setState('engage'); return; }
    }

    // Advance along the route, then to the objective itself.
    let goal = obj.pos;
    let onRoute = false;
    if (this.route && this.routeIdx < this.route.length) {
      const wp = this.route[this.routeIdx];
      if (this.root.position.distanceTo(wp) < 2.2) {
        this.routeIdx++;
        this.routeStuck = 0;
        this._lastProg = null;
      } else {
        goal = wp;
        onRoute = true;
      }
    }

    const dist = this.root.position.distanceTo(obj.pos);
    if (dist <= (obj.radius ?? 2.5)) {
      // Arrived. Tell the mode we are ready to plant or defuse; it owns the
      // timers so bots and the player share one implementation.
      this.interact = obj.kind;
      this._aimAt(obj.pos);
      return;
    }
    this.interact = null;

    /*
     * Progress watchdog.
     *
     * Reactive steering buries a bot in a concave corner — measured on Dust II
     * with five of eight whiskers blocked and 0.4 m/s against a 4.3 m/s run.
     * Skipping to the next waypoint (the obvious recovery) is actively wrong:
     * it aims the bot further into the wall and burns the whole chain. A fresh
     * route from the wedge point does exist, so ask for one, and reverse out
     * far enough that the new route is actually walkable.
     */
    const prog = this.root.position.distanceToSquared(goal);
    if (this._lastProg == null || prog < this._lastProg - 0.25) {
      this._lastProg = prog;
      this.routeStuck = 0;
    } else {
      this.routeStuck = (this.routeStuck ?? 0) + dt;
      if (this.routeStuck > 2.0) {
        this.needsRepath = true;
        this.unwedge = 0.8;
        this.routeStuck = 0;
        this._lastProg = null;
      }
    }

    if (this.unwedge > 0) {
      this.unwedge -= dt;
      // Straight back the way we came, ignoring whiskers — they are what got
      // us in here.
      _dir.subVectors(this.root.position, goal).setY(0).normalize();
      this.velocity.addScaledVector(_dir, Math.min(24 * dt, this.speeds.walk));
      return;
    }

    this._moveToward(goal, this.speeds.run, dt, onRoute ? 1.2 : 2.6);
  }

  /** Give this bot an objective and the route to reach it. */
  setObjective(kind, pos, route = null, radius = 2.5) {
    // Idempotent on purpose. The mode reassigns every second; if that reset the
    // route each time, the bot would restart the chain from wherever it stood
    // and orbit the first corner forever — measured as a hard stall 52 m short
    // of the site. Same job, same destination: keep the progress.
    const same = this.objective && this.objective.kind === kind
      && this.objective.pos.distanceToSquared(pos) < 4;
    if (same) {
      this.objective.radius = radius;
      this.objective.pos.copy(pos);
      if (this.state !== 'engage' && this.state !== 'cover') this.setState('objective');
      return;
    }

    this.objective = { kind, pos: pos.clone(), radius };
    this.route = route ? route.map((p) => p.clone()) : null;
    this.routeIdx = 0;
    this.routeStuck = 0;
    if (this.state !== 'engage' && this.state !== 'cover') this.setState('objective');
  }

  clearObjective() {
    this.committed = false;
    this.objective = null;
    this.route = null;
    this.interact = null;
  }

  /* ------------------------------------------------------------ engage --- */

  _engage(dt, sees) {
    const player = this.actor;
    if (!player) { this.setState('patrol'); return; }

    // An objective-holding bot must not stand and trade forever at long range:
    // it engaged because it SAW someone, but the round is won by moving. If the
    // foe is far or no longer visible, go back to the job.
    if (this.objective) {
      const far = this.root.position.distanceTo(Enemy.aimPoint(player, _v3)) > 45;
      if (far || (!sees && this.stateTime > 2.5)) {
        this.setState('objective');
        return;
      }
    }
    const dist = this.root.position.distanceTo(Enemy.aimPoint(player, _v3));
    this.reaction -= dt;

    if (!sees) {
      if (this.stateTime > 1.4) { this.setState('search'); return; }
      this._aimAt(Enemy.aimPoint(player, _v3));
      this._moveToward(this.lastKnown, this.speeds.run, dt);
      // Suppressing fire into the last known position.
      this._maybeSuppress(dt);
      return;
    }

    this._aimAt(Enemy.aimPoint(player, _v3));

    // Suppressed or non-assault roles want cover — unless this bot just gave
    // up on a slot, in which case it fights from where it is.
    if (this.coverCooldown <= 0
      && (this.suppressed > 0.45 || this.role === 'suppress' || this.role === 'flank')) {
      if (this._acquireCover(dist)) { this.setState('cover'); return; }
    }

    this.repathTimer -= dt;
    if (this.repathTimer <= 0) {
      this.repathTimer = 1.1 + Math.random() * 1.4;
      this.strafeDir = Math.random() < 0.5 ? -1 : 1;
    }

    const ideal = this.role === 'assault' ? 9 : 16;
    const move = _v.set(0, 0, 0);
    _dir.copy(Enemy.aimPoint(player, _v3)).sub(this.root.position).setY(0);
    const distXZ = _dir.length() || 1;
    _dir.divideScalar(distXZ);
    if (distXZ > ideal + 3) move.add(_dir);
    else if (distXZ < ideal - 4) move.sub(_dir);
    move.x += -_dir.z * this.strafeDir * 0.9;
    move.z += _dir.x * this.strafeDir * 0.9;

    /*
     * Sprint only when there is nothing to shoot at.
     *
     * `_tryFire` refuses to fire while `sprintFlag` is up, so the old rule —
     * sprint whenever an assault bot is past `ideal + 10` = 19 m — made every
     * assault bot MUTE from 19 m out to the 75 m its difficulty preset says it
     * engages to. `duel-probe` measured that at 27-63% of all the frames a bot
     * had line of sight to a foe inside its own range: it could see you, it
     * could hit you, and it was holding the trigger because it was jogging.
     *
     * Closing fast is still right when firing is pointless, so the gate is now
     * the range the preset actually engages at rather than a fixed 19 m. Inside
     * that it runs and shoots, which is what the whole fight was missing.
     */
    const sprinting = this.role === 'assault'
      && distXZ > Math.max(ideal + 10, this.skill.maxRange);
    if (move.lengthSq() > 1e-4) {
      move.normalize();
      this._steer(move, sprinting ? this.speeds.sprint : distXZ > ideal + 3 ? this.speeds.run : this.speeds.walk, dt);
    } else {
      this.velocity.x *= Math.exp(-8 * dt);
      this.velocity.z *= Math.exp(-8 * dt);
    }
    this.sprintFlag = sprinting;

    this._tryFire(dt, dist, sees, 1);
  }

  /* ------------------------------------------------------------- cover --- */

  /**
   * Claim a barrier to fight from behind, or report that there isn't one.
   *
   * ## The standing spot has to be covered, and nothing used to check it
   *
   * A cover point names a BARRIER; the bot stands 0.75 m behind it, on the
   * opposite side from its foe, and leans out to shoot. That only works if the
   * foe is on the far side of the barrier — and the old scoring never asked.
   * It scored candidates on being near the bot and roughly 20 m from the foe,
   * which selects points all over the map whose barrier is nowhere between the
   * two. The standing spot then landed INSIDE the geometry (0.75 m into a wall
   * the bot was already standing against), the bot could never physically
   * occupy it, and `_cover` walked it at that spot until something killed it.
   *
   * So the test is now the definition: cast from the standing spot at the foe
   * and require it to hit something close. That one ray is what makes a
   * candidate cover rather than a coordinate, and it is also what keeps the
   * spot reachable — a barrier between the bot and the foe is, by
   * construction, a barrier the bot is standing on its own side of.
   *
   * Cheap enough to do honestly: this runs on a state transition, not per
   * frame, and the distance filter leaves only a handful of candidates.
   */
  _acquireCover(dist) {
    const pts = this.world.coverPoints?.() ?? this.world.level?.coverPoints;
    if (!pts || !pts.length) return false;
    if (this.cover) return true;
    if (!this.actor) return false;
    const claims = boardFor(this.team).coverClaims;
    const physics = this.world.physics;
    const nav = this.world.nav?.();
    const pp = Enemy.aimPoint(this.actor, _v3).clone();
    let best = -1, bestScore = -Infinity, bestStand = null;
    for (let i = 0; i < pts.length; i++) {
      if (claims.has(i) && claims.get(i) !== this.id) continue;
      const c = pts[i];
      const d = this.root.position.distanceTo(c);
      /*
       * 9 m, down from 34.
       *
       * Cover is for the fight the bot is already in; a slot a third of a map
       * away is a cross-country walk, not a tactic. The number is set by how
       * many approaches finish: at 14 m the bots reached one claim in nine and
       * abandoned the rest, at 9 m they reach six in seven. The walk is routed,
       * so distance costs more than it looks — a 14 m claim is often a 25 m
       * path around a building.
       */
      if (d > 9) continue;
      const dp = c.distanceTo(pp);
      // Where it would stand: behind the barrier, away from the foe.
      _v.subVectors(c, pp).setY(0);
      if (_v.lengthSq() < 1e-4) continue;
      _v.normalize();
      _v2.copy(c).addScaledVector(_v, 0.75);
      /*
       * The bot has to be on the covered side ALREADY.
       *
       * If the foe is on the same side of the barrier as the bot, the standing
       * spot lands on the far side of it, and reaching it means walking around
       * — a 14 m claim turning into a 40 m detour, which `duel-probe` measured
       * as gaps that GREW to 32 m and seven give-ups against zero arrivals on
       * Dust II. The spot being nearer to the bot than the barrier is exactly
       * the statement "the barrier is between us and the foe, not behind us",
       * and it costs one comparison.
       */
      if (this.root.position.distanceTo(_v2) > d + 0.5) continue;
      /*
       * And it has to be somewhere the bot can walk to.
       *
       * `nav.route` returns null for a goal off the level's main walkable body
       * and `_navToward` then quietly degrades to straight-line whiskers — the
       * exact behaviour the routing work replaced. Asking the grid first turns a
       * slot the bot would have spent its whole approach budget failing to reach
       * into a candidate it never claims.
       */
      if (nav && !nav.inMain(_v2)) continue;
      // Standing chest height: can a body actually occupy that spot?
      _v2.y = this.root.position.y + 0.9;
      if (physics.insideSolid(_v2, 1.0)) continue;   // that spot is a wall
      /*
       * THE test: is the barrier actually between that spot and the foe?
       *
       * Cast from CROUCH height, because that is the height the bot hides at —
       * `_cover` crouches whenever it is not leaning out. Probing at standing
       * chest instead sent the ray straight over every sandbag line and low
       * crate on the map and rejected them all: blackout went from thousands of
       * frames in cover to zero, which is not "cover is now selective", it is
       * cover being unreachable.
       */
      _v2.y = this.root.position.y + 0.55;
      _dir.copy(pp).sub(_v2);
      const reach = _dir.length() || 1;
      _dir.divideScalar(reach);
      const blocker = physics.raycast(_v2, _dir, Math.min(reach, 3.0));
      if (!blocker) continue;                        // open ground, not cover
      // Want cover that is near us, faces the player, and is at a fighting range.
      let score = -d * 1.0 - Math.abs(dp - (this.role === 'flank' ? 14 : 20)) * 0.6;
      if (this.role === 'flank') {
        _v.subVectors(c, pp).setY(0).normalize();
        _v2.subVectors(this.root.position, pp).setY(0).normalize();
        score += (1 - _v.dot(_v2)) * 16;   // reward a different angle of attack
      }
      if (score > bestScore) {
        bestScore = score;
        best = i;
        // Recomputed rather than carried out of the loop: the flank branch
        // above reuses `_v`, and the one allocation here happens on a state
        // transition, not in the per-frame path the no-allocation rule is about.
        _v.subVectors(c, pp).setY(0).normalize();
        bestStand = c.clone().addScaledVector(_v, 0.75);
      }
    }
    if (best < 0) return false;
    claims.set(best, this.id);
    this.cover = {
      index: best,
      pos: pts[best].clone(),
      stand: bestStand,
      peekSide: Math.random() < 0.5 ? -1 : 1,
    };
    void dist;
    return true;
  }

  _releaseCover() {
    if (this.cover) boardFor(this.team).coverClaims.delete(this.cover.index);
    this.cover = null;
    this.peek = 0;
  }

  _cover(dt, sees) {
    const player = this.actor;
    if (!player) { this._releaseCover(); this.setState('patrol'); return; }
    if (!this.cover) { this.setState('engage'); return; }
    if (this.role === 'assault' && this.suppressed < 0.3) { this._releaseCover(); this.setState('engage'); return; }
    if (this.alert < 0.25) { this._releaseCover(); this.setState('patrol'); return; }

    const stand = this.cover.stand;
    const d = this.root.position.distanceTo(stand);
    this._aimAt(sees ? Enemy.aimPoint(player, _v3) : this.lastKnown);

    /*
     * Give up on a cover slot it cannot get to.
     *
     * This check HAS to come before the approach below, and that is the whole
     * bug: the "rotate cover occasionally" release at the bottom of this method
     * is unreachable while `d > 1.0` returns early, so a bot that never arrives
     * never leaves. `duel-probe` measured the result — a median gap of 9.7 m to
     * its own standing spot, one bot walking at a slot for 32.3 unbroken
     * seconds, and `cover` burning more bot-frames than `engage`. It was not
     * taking cover, it was leaving the match.
     *
     * Eight seconds, on a flat clock — and the obvious cleverer rule is wrong.
     * Giving up when the straight-line gap stops shrinking abandoned nearly
     * every approach, because the walk is ROUTED: a planned route goes around
     * geometry, so the straight-line distance grows for seconds at a stretch
     * while real progress is being made. Measured on Assault at 2412 frames
     * opening the gap against 1129 closing it, on bots moving at 4.6 m/s and
     * perfectly fine. `_navToward` already owns genuine wedging, with its own
     * repath watchdog; this only has to be the outer bound.
     */
    if (d > 1.0 && this.stateTime > 8) {
      this._releaseCover();
      this.coverCooldown = 5 + Math.random() * 4;
      this.setState('engage');
      return;
    }

    if (d > 1.0) {
      /*
       * Routed, not steered. Cover is picked up to 14 m away and is very often
       * through a doorway or around a corner — exactly the case straight-line
       * whiskers cannot solve, and the same reason `_patrol` and `_search`
       * already plan. The old `_moveToward` here is most of why the gap above
       * was 9.7 m and not 1.1 m.
       */
      this._navToward(stand, d > 6 ? this.speeds.sprint : this.speeds.run, dt, 1.0);
      this.sprintFlag = d > 6;
      this.peek = damp(this.peek, 0, 6, dt);
      this.crouched = false;
      return;
    }

    this.sprintFlag = false;
    this.velocity.x *= Math.exp(-10 * dt);
    this.velocity.z *= Math.exp(-10 * dt);

    // Peek cycle: hunker, pop up, shoot, drop back.
    this.peekTimer -= dt;
    if (this.peekTimer <= 0) {
      const exposed = this.peek > 0.5;
      this.peek = exposed ? 0 : 1;
      // Peeking longer when unsuppressed and the squad needs pressure.
      this.peekTimer = exposed
        ? 0.9 + Math.random() * 1.6 + this.suppressed * 1.6
        : 1.0 + Math.random() * 1.4;
      if (!exposed) this.burst = 0;
      if (exposed) this.cover.peekSide = Math.random() < 0.5 ? -1 : 1;
    }
    this.peekBlend = damp(this.peekBlend ?? 0, this.peek, 8, dt);
    this.crouched = this.peekBlend < 0.5;

    // Lateral lean out of the side of the barrier while exposed.
    _v.set(-Math.cos(this.yaw), 0, Math.sin(this.yaw))
      .multiplyScalar(this.cover.peekSide * 0.5 * this.peekBlend);
    _v2.copy(stand).add(_v);
    if (this.root.position.distanceTo(_v2) > 0.25) this._moveToward(_v2, this.speeds.walk, dt);

    this.reaction -= dt;
    if (this.peekBlend > 0.6 && sees) {
      this._tryFire(dt, this.root.position.distanceTo(Enemy.aimPoint(player, _v3)), true, 1);
    } else if (this.peekBlend > 0.6) {
      this._maybeSuppress(dt);
    }

    // Rotate cover occasionally so the fight moves.
    if (this.stateTime > 9 + Math.random() * 6) {
      this._releaseCover();
      this.setState('engage');
    }
  }

  /* ------------------------------------------------------------ search --- */

  _planSearch() {
    this.searchPoints.length = 0;
    const base = this.lastKnown;
    const n = 3;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 3 + Math.random() * 9;
      this.searchPoints.push(new THREE.Vector3(
        base.x + Math.cos(a) * r, base.y, base.z + Math.sin(a) * r
      ));
    }
    this.searchIdle = 0;
  }

  _search(dt, sees) {
    if (sees && this.alert > 0.5) { this.setState('engage'); return; }
    this.crouched = false;
    if (!this.searchPoints.length) { this.setState('patrol'); return; }
    const t = this.searchPoints[0];
    const d = this.root.position.distanceTo(t);
    if (d < 1.8) {
      // Stop and sweep before moving on — reads as a real search.
      this.searchIdle += dt;
      this.velocity.x *= Math.exp(-8 * dt);
      this.velocity.z *= Math.exp(-8 * dt);
      this.targetYaw = this.yaw + Math.sin(this.searchIdle * 1.6) * 0.05;
      this.aimYaw = this.yaw + Math.sin(this.searchIdle * 1.1) * 0.9;
      if (this.searchIdle > 1.6) {
        this.searchPoints.shift();
        this.searchIdle = 0;
        this.needsRepath = true;
      }
    } else {
      // Routed, like patrol. A search point is picked in a ring around the last
      // known position and is very often through a doorway from here, which is
      // the case straight-line steering cannot solve.
      this._navToward(t, this.speeds.run, dt, 1.8);
      this.aimYaw = damp(this.aimYaw, this.targetYaw, 5, dt);
    }
    if (this.stateTime > 14) this.setState('patrol');
  }

  /* ------------------------------------------------------------ firing --- */

  _aimAt(target) {
    _dir.copy(target).sub(this.root.position);
    const flat = Math.hypot(_dir.x, _dir.z);
    this.aimYaw = Math.atan2(-_dir.x, -_dir.z);
    const eye = this.eyePosition;
    this.aimPitch = clamp(Math.atan2(target.y + 0.35 - eye.y, flat), -0.8, 0.8);
    // The body follows the aim, but lazily — that's the upper/lower split.
    const delta = wrapPi(this.aimYaw - this.targetYaw);
    if (Math.abs(delta) > 0.9) this.targetYaw = this.aimYaw;
  }

  _maybeSuppress(dt) {
    if (this.role === 'assault') return;
    /*
     * Not from inside a wall.
     *
     * Suppression deliberately fires WITHOUT line of sight — that is the whole
     * point of it, and `_cover`/`_engage` hand `_tryFire` a hard-coded
     * `sees = true` on this path. So fixing `canSeeTarget` did not close the
     * hole: `stuck-probe` still caught an embedded bot getting two rounds off
     * on Dust II. A bot buried in geometry cannot suppress anything; every
     * round it fires spawns its impact FX inside the brush it is standing in.
     */
    if (this._embedded) return;
    this.fireTimer -= dt;
    if (SQUAD.time - (this.lastSeen ?? -99) > 6) return;
    if (this.fireTimer <= 0 && this.ammo > 0) {
      if (this.burst <= 0) {
        this.burst = 2 + ((Math.random() * 3) | 0);
        this.fireTimer = 0.8 + Math.random() * 1.6;
      } else {
        this._shoot(0.075);       // deliberately wide — suppression, not hits
        this.burst--;
        this.fireTimer = this.burst > 0 ? 0.10 : 1.2 + Math.random();
      }
    }
  }

  _tryFire(dt, dist, sees, accScale) {
    this.fireTimer -= dt;
    if (this.reloading > 0) return;
    if (this.ammo <= 0) { this._startReload(); return; }
    if (this.reaction > 0 || !sees || dist > this.skill.maxRange) return;

    // Don't fire while sprinting.
    if (this.sprintFlag) return;

    if (this.fireTimer <= 0) {
      if (this.burst <= 0) {
        this.burst = 3 + ((Math.random() * 3) | 0);
        // Longer settle between bursts at range; snappier up close.
        this.fireTimer = clamp(0.18 + dist * 0.012, 0.18, 0.55);
        this.adsPulse = 1;
      } else {
        // First burst after acquiring is deliberately loose so it feels fair.
        const spread = (this.firstBurst ? this.skill.burstSpread : 1) * accScale
          * (1 + this.suppressed * (1.2 / this.skill.suppressResist))
          * (1 + clamp(dist / 60, 0, 1) * 0.7);
        this._shoot(this.accuracy * spread);
        this.burst--;
        this.ammo--;
        if (this.burst <= 0) {
          this.firstBurst = false;
          this.fireTimer = 0.55 + Math.random() * 0.85;
        } else this.fireTimer = 0.085;
      }
    }
  }

  _startReload() {
    if (this.reloading > 0) return;
    this.reloading = 2.1;
    this.burst = 0;
    this.fireTimer = 2.2;
  }

  _shoot(cone) {
    const world = this.world;
    const p = this.parts;
    p.muzzle.updateWorldMatrix(true, false);
    const origin = new THREE.Vector3().setFromMatrixPosition(p.muzzle.matrixWorld);
    const foe = this.actor;
    if (!foe) return;
    const target = Enemy.aimPoint(foe, new THREE.Vector3());
    target.y -= 0.25;
    // Lead error against a moving target keeps it human.
    const pv = foe.velocity;
    if (pv) target.addScaledVector(pv, (Math.random() - 0.6) * 0.08);
    const dir = target.sub(origin);
    const dist = dir.length() || 1;
    dir.divideScalar(dist);
    dir.x += (Math.random() - 0.5) * cone * 2;
    dir.y += (Math.random() - 0.5) * cone * 2;
    dir.z += (Math.random() - 0.5) * cone * 2;
    dir.normalize();

    this.recoilVel += 9;

    const hit = world.physics.raycast(origin, dir, 120);
    const end = hit ? hit.point : origin.clone().addScaledVector(dir, 120);
    world.effects.tracer(origin, end, 520);
    world.effects.spark(origin, dir, 3, 0.5, 0.6);
    world.effects.muzzleLightAt?.(origin);
    if (hit) world.effects.impact(hit.point, hit.normal, 'concrete');

    // Who did this round hit? `world.hitscanVictim` tests every actor on the
    // opposing side, so bot-vs-bot fire uses exactly the same path as
    // bot-vs-player. Previously only the player's capsule was tested, which is
    // why bots could never damage each other.
    const maxDist = hit ? hit.distance : 120;
    const victim = world.hitscanVictim
      ? world.hitscanVictim(origin, dir, maxDist, this)
      : this._legacyPlayerHit(origin, dir, hit);
    if (victim) {
      const amount = 9 + Math.random() * 7;
      // Read before the hit: the award stats want what this round actually took
      // off, and the player's damage() does not report through world.onDamage
      // the way an Enemy's does.
      const before = victim.health ?? 0;
      victim.damage(amount, 'body', dir, this);
      if (!victim.isEnemy) world.onPlayerHit?.(origin, this, Math.min(amount, before));
    }
    world.onShotFired?.(origin, this.team, this);
    world.audio?.playAt('enemyShot', origin);
  }

  /** Fallback player-only hit test, for hosts without `world.hitscanVictim`. */
  _legacyPlayerHit(origin, dir, hit) {
    const player = this.world.player;
    if (!player) return null;
    const to = player.position.clone().sub(origin);
    const proj = to.dot(dir);
    if (proj <= 0 || (hit && proj >= hit.distance)) return null;
    const perp = to.addScaledVector(dir, -proj).length();
    return perp < 0.45 ? player : null;
  }

  /* ---------------------------------------------------------- movement --- */

  _moveToward(target, speed, dt, probe = 2.6) {
    _v.copy(target).sub(this.root.position).setY(0);
    if (_v.lengthSq() < 0.04) return;
    _v.normalize();
    this.moveDir = this.moveDir || new THREE.Vector3();
    this.moveDir.copy(_v);
    if (this.state !== 'engage' && this.state !== 'cover') {
      this.targetYaw = Math.atan2(-_v.x, -_v.z);
      this.aimYaw = damp(this.aimYaw, this.targetYaw, 6, dt);
    }
    this._steer(_v, speed, dt, probe);
  }

  /**
   * `range` is how far ahead the whiskers look. It drops when the bot is
   * walking a precomputed route: that path is already known to be walkable, so
   * long-range avoidance has nothing to add and plenty to break — in a corridor
   * the forward whisker hits the far wall of every bend and turns the bot out
   * of the passage it is trying to enter.
   */
  _steer(dir, speed, dt, range = 2.6) {
    const eye = _v3.copy(this.eyePosition);
    const probe = (angle) => {
      _v2.copy(dir).applyAxisAngle(_up, angle);
      const hit = this.world.physics.raycast(eye, _v2, range);
      return hit ? 1 - hit.distance / range : 0;
    };
    const left = probe(0.6), right = probe(-0.6), center = probe(0);
    if (center > 0.1 || left > 0.1 || right > 0.1) {
      const turn = (left - right) * 1.15 + (center > 0.35 ? this.strafeDir * 1.25 : 0);
      dir.applyAxisAngle(_up, -turn * 0.9).normalize();
    }
    // Separation so the squad doesn't stack up in a conga line.
    for (const m of boardFor(this.team).members) {
      if (m === this || !m.alive) continue;
      _v2.subVectors(this.root.position, m.root.position).setY(0);
      const d2 = _v2.lengthSq();
      if (d2 < 1.6 && d2 > 1e-4) dir.addScaledVector(_v2.normalize(), 0.55);
    }
    dir.normalize();

    // Remember what we were TRYING to do — `_integrate` needs the intent, not
    // the outcome, to tell "blocked" apart from "standing still".
    this.wantSpeed = speed;

    const accel = 24;
    const current = this.velocity.dot(dir);
    const add = Math.min(accel * dt, Math.max(0, speed - current));
    this.velocity.addScaledVector(dir, add);
    const hs = Math.hypot(this.velocity.x, this.velocity.z);
    if (hs > speed) {
      const k = speed / hs;
      this.velocity.x *= k;
      this.velocity.z *= k;
    }
  }

  _integrate(dt) {
    this.velocity.y -= this.world.physics.gravity * dt;
    if (this.grounded) {
      this.velocity.x *= Math.exp(-4.5 * dt);
      this.velocity.z *= Math.exp(-4.5 * dt);
    }
    const before = _v3.copy(this.capsule.start);
    this.capsule.translate(_v.copy(this.velocity).multiplyScalar(dt));
    const res = this.world.physics.collideCapsule(this.capsule, this.velocity);
    this.grounded = res.grounded;

    // Bots leave the world the same ways players do, and a bot falling forever
    // is worse than a player doing it: nothing can shoot it, so an elimination
    // round never ends. Same last-safe-ground recovery, tracked the same way.
    if (this.grounded && Math.abs(this.velocity.y) < 0.5) {
      (this._safeGround ??= new THREE.Vector3()).copy(this.capsule.start);
    }
    const killY = this.world.physics.killY;
    // The same two tests the player gets: below every triangle in the world, or
    // standing somewhere the map's own clip hull forbade. A bot on a fenced-off
    // roof is not just cosmetically wrong — it is a bot the other side may
    // never be able to reach, and an elimination round that never ends.
    const hull = this.world.physics.clipHull;
    let outOfBounds = false;

    /*
     * Embedded in solid geometry, which is its own way out of the world.
     *
     * A bot inside a brush is unreachable — the player's rounds stop on the
     * near face — so in an elimination round it is a bot that can never be
     * killed and a round that can never end. Symmetric LOS stops it SHOOTING
     * from in there (see `canSeeTarget`), but a silent invulnerable statue is
     * still broken, so it has to be moved.
     *
     * Sampled at 6 Hz, not per frame: `insideSolid` is up to eight raycasts and
     * this is a recovery path, not a control input. A quarter of a second of
     * being inside a wall is not something anybody can perceive.
     */
    this._solidCheck = (this._solidCheck ?? Math.random() * 0.17) - dt;
    // `canSeeTarget` may have resolved it lazily before the first tick; from
    // here on this sampler is the owner.
    if (this._solidCheck <= 0) {
      this._solidCheck = 0.17;
      this._embedded = this.world.physics.insideSolid(this.capsule.start, 1.2);
    }
    if (this._embedded) {
      this._stuckFor = (this._stuckFor ?? 0) + dt;
      if (this._stuckFor > 0.5) outOfBounds = true;
    } else this._stuckFor = 0;

    if (hull && this.grounded) {
      const feetY = this.capsule.start.y - this.capsule.radius;
      if (hull.forbidsStanding(this.capsule.start.x, feetY, this.capsule.start.z)) {
        this._oobFor = (this._oobFor ?? 0) + dt;
        outOfBounds = this._oobFor >= 1.0;
      } else this._oobFor = 0;
    } else this._oobFor = 0;

    if ((killY != null && this.capsule.start.y < killY) || outOfBounds) {
      this._oobFor = 0;
      this._stuckFor = 0;
      this._embedded = false;
      if (this._safeGround) {
        const h = this.capsule.end.y - this.capsule.start.y;
        this.capsule.start.copy(this._safeGround);
        this.capsule.end.copy(this._safeGround).y += h;
        this.velocity.set(0, 0, 0);
        this.grounded = true;
      } else {
        // Spawned into the void and never stood anywhere: killing it is the
        // only exit that leaves the round winnable.
        this.damage(9999, 'body', null, null);
      }
    }

    /*
     * Mantle: trying to move but going nowhere -> hop the obstacle.
     *
     * `wanted` is the speed the AI ASKED for, not the speed it achieved. Using
     * the achieved speed made this a dead zone exactly when it was needed: a
     * bot pressed against a 0.8 m lip moves at 0.4 m/s, which is below the
     * threshold, so the recovery never fired and it leaned on the kerb for the
     * rest of the round.
     */
    const wanted = Math.max(this.wantSpeed ?? 0,
      Math.hypot(this.velocity.x, this.velocity.z));
    this.wantSpeed = 0;
    const moved = Math.hypot(this.capsule.start.x - before.x, this.capsule.start.z - before.z) / Math.max(dt, 1e-4);
    /*
     * Accumulate blocked-ness with a decay rather than a hard reset, and do not
     * require `grounded` to accumulate it — only to act on it.
     *
     * A bot leaning on a step bounces, so `grounded` flickers false every few
     * frames. With a hard reset that zeroed the timer before it could ever
     * reach the threshold: measured peak 0.03 s against a 0.25 s trigger, i.e.
     * the mantle could not fire at all in the one situation it exists for.
     */
    const stuck = wanted > 1.2 && moved < wanted * 0.35;
    if (stuck) this.blockedTime = (this.blockedTime ?? 0) + dt;
    else this.blockedTime = Math.max(0, (this.blockedTime ?? 0) - dt * 2);

    if (this.grounded && stuck) {
      if (this.blockedTime > 0.2 && this.mantle <= 0) {
        // apex = v^2 / 2g = 7.3^2 / 48 = 1.11 m. The old 5.4 gave 0.61 m,
        // which does not clear the 0.8 m lips these maps are full of — the bot
        // hopped, fell exactly short, and hopped again for the whole round.
        // Still well under the player's 1.6 m mantle.
        this.velocity.y = 7.3;
        this.mantle = 0.75;
        this.blockedTime = 0;
      }
    }
    this.mantle = Math.max(0, this.mantle - dt);

    this.root.position.copy(this.capsule.start).setY(this.capsule.start.y - 0.34);

    // Turn-in-place: track how fast we're rotating for the turn clip.
    const d = wrapPi(this.targetYaw - this.yaw);
    const rate = clamp(d * 4.5, -3.2, 3.2);
    this.turnRate = damp(this.turnRate ?? 0, rate, 8, dt);
    this.yaw += clamp(d, -Math.abs(rate) * dt, Math.abs(rate) * dt);
    this.root.rotation.y = this.yaw;
  }

  /* --------------------------------------------------------- animation --- */

  _animate(dt) {
    const p = this.parts;
    const b = p.bones;
    const speed = Math.hypot(this.velocity.x, this.velocity.z);

    // --- decide the locomotion blend from velocity relative to facing.
    const fx = -Math.sin(this.yaw), fz = -Math.cos(this.yaw);
    const rx = -fz, rz = fx;
    const fwd = this.velocity.x * fx + this.velocity.z * fz;
    const side = this.velocity.x * rx + this.velocity.z * rz;

    const moving = speed > 0.25;
    const wantCrouch = this.crouched;
    this.crouchBlend = damp(this.crouchBlend, wantCrouch ? 1 : 0, 7, dt);

    const bl = this.blend;
    const target = { idle: 0, walk: 0, run: 0, sprint: 0, crouch: 0, strafeL: 0, strafeR: 0, turn: 0 };
    if (!moving) {
      if (Math.abs(this.turnRate ?? 0) > 0.7) {
        target.turn = 1;
        this.turnSign = Math.sign(this.turnRate);
      } else target.idle = 1;
      if (wantCrouch) { target.crouch = 1; target.idle = 0; target.turn = 0; }
    } else if (wantCrouch) {
      target.crouch = 1;
    } else {
      const sideFrac = clamp(Math.abs(side) / Math.max(speed, 0.001), 0, 1);
      const lateral = sideFrac > 0.55 && fwd < 2.2;
      if (lateral) {
        if (side > 0) target.strafeL = 1; else target.strafeR = 1;
      } else if (this.sprintFlag && speed > this.speeds.run * 0.9) {
        target.sprint = 1;
      } else if (speed > this.speeds.walk * 1.25) {
        target.run = clamp((speed - this.speeds.walk) / (this.speeds.run - this.speeds.walk), 0, 1);
        target.walk = 1 - target.run;
      } else {
        target.walk = clamp(speed / this.speeds.walk, 0, 1);
        target.idle = 1 - target.walk;
      }
      // Moving backwards reverses the gait so the feet still track the ground.
      this.gaitSign = fwd < -0.4 ? -1 : 1;
    }
    let sum = 0;
    for (const k of Object.keys(bl)) {
      bl[k] = damp(bl[k], target[k], 10, dt);
      sum += bl[k];
    }
    if (sum > 1e-4) for (const k of Object.keys(bl)) bl[k] /= sum;

    // --- phase advances with distance travelled: no foot skating.
    const stride = wantCrouch ? 1.05 : speed > this.speeds.run * 0.85 ? 2.35 : 1.55;
    if (moving) {
      this.gaitPhase += (speed * dt / stride) * Math.PI * 2 * (this.gaitSign ?? 1);
    } else if (bl.turn > 0.2) {
      this.gaitPhase += dt * 4.5 * this.turnSign;
    } else {
      // Ease the phase back to a neutral stance instead of freezing mid-stride.
      const tgt = Math.round(this.gaitPhase / Math.PI) * Math.PI;
      this.gaitPhase = damp(this.gaitPhase, tgt, 6, dt);
    }
    const ph = this.gaitPhase;

    // --- accumulate the locomotion pose.
    const acc = zeroPose(_accum);
    const s = _scratchPose;
    if (bl.idle > 1e-3) addPose(acc, clipIdle(s, this.animTime, 1), bl.idle);
    if (bl.walk > 1e-3) addPose(acc, clipWalk(s, ph, 1), bl.walk);
    if (bl.run > 1e-3) addPose(acc, clipRun(s, ph), bl.run);
    if (bl.sprint > 1e-3) addPose(acc, clipSprint(s, ph), bl.sprint);
    if (bl.crouch > 1e-3) addPose(acc, clipCrouchWalk(s, ph, moving ? 1 : 0.15), bl.crouch);
    if (bl.strafeL > 1e-3) addPose(acc, clipStrafe(s, ph, 1), bl.strafeL);
    if (bl.strafeR > 1e-3) addPose(acc, clipStrafe(s, ph, -1), bl.strafeR);
    if (bl.turn > 1e-3) addPose(acc, clipTurn(s, ph, this.turnSign), bl.turn);

    // --- weapon carry: pick the mount pose and let IK place the hands.
    const engaged = (this.state === 'engage' || this.state === 'cover' || this.alert > 0.5) ? 1 : 0;
    const wantAim = engaged && !this.sprintFlag ? 1 : 0;
    this.aimBlend = damp(this.aimBlend, wantAim, 7, dt);
    const wantAds = (this.state === 'engage' || this.state === 'cover')
      && this.reaction <= 0 && this.alert > 0.6 && !this.sprintFlag && this.reloading <= 0 ? 1 : 0;
    this.adsBlend = damp(this.adsBlend, wantAds, 6, dt);
    this.sprintBlend = damp(this.sprintBlend ?? 0, this.sprintFlag ? 1 : 0, 7, dt);
    this.reloadBlend = damp(this.reloadBlend ?? 0, this.reloading > 0 ? 1 : 0, 8, dt);

    // Aiming leans the shoulders into the gun and brings the head over the
    // stock — without this the model stands bolt upright behind the rifle.
    const ab = this.aimBlend, adb = this.adsBlend;
    acc.clavLZ += -0.05 * ab;
    acc.clavRZ += -0.12 * ab;
    acc.chestRY += -0.15 * ab;
    acc.spineRY += -0.05 * ab;
    acc.spineRX += 0.10 * adb;
    acc.chestRX += 0.07 * adb;
    acc.neckRX += 0.13 * adb;
    acc.neckRZ += -0.10 * adb;
    acc.hipsRX += 0.05 * adb;
    // Fighting stance: knees soft, weight forward, feet slightly apart.
    if (!moving) {
      acc.hipLX += 0.14 * ab; acc.hipRX += 0.10 * ab;
      acc.knLX += 0.30 * ab; acc.knRX += 0.24 * ab;
      acc.anLX += -0.16 * ab; acc.anRX += -0.12 * ab;
      acc.hipLZ += -0.032 * ab; acc.hipRZ += 0.038 * ab;
      acc.hipsY += -0.045 * ab;
    }

    this.reloading = Math.max(0, this.reloading - dt);
    if (this.reloading === 0 && this.ammo <= 0) this.ammo = 30;

    // --- recoil travels from the gun up the arms into the torso.
    this.recoilVel += -this.recoil * 260 * dt;
    this.recoilVel *= Math.exp(-13 * dt);
    this.recoil += this.recoilVel * dt;
    const rc = this.recoil;
    acc.chestRX -= rc * 0.12;
    acc.spineRX -= rc * 0.06;
    acc.neckRX -= rc * 0.07;
    acc.chestRZ += rc * 0.04;

    // --- hit flinch (spring, per body region).
    this.flinchVel.multiplyScalar(Math.exp(-9 * dt));
    this.flinchVel.addScaledVector(this.flinch, -55 * dt);
    this.flinch.addScaledVector(this.flinchVel, dt);
    const f = this.flinch;
    const headHit = this.hitRegion === 'head' ? 1 : 0;
    acc.chestRX += f.x * (1 - headHit * 0.4);
    acc.chestRY += f.y * 0.6;
    acc.chestRZ += f.z * 0.5;
    acc.spineRX += f.x * 0.5;
    acc.neckRX += f.x * (0.7 + headHit * 1.2);
    acc.neckRZ += f.z * (0.5 + headHit * 1.0);
    acc.hipsRX += f.x * 0.25;

    // Suppression makes them hunch and duck.
    const sup = this.suppressed;
    acc.hipsY -= sup * 0.10;
    acc.spineRX += sup * 0.14;
    acc.neckRX += sup * 0.10;

    // --- upper/lower split: torso twists toward the aim direction.
    const twist = wrapPi(this.aimYaw - this.yaw);
    const twistClamped = clamp(twist, -1.15, 1.15);
    acc.spineRY += twistClamped * 0.30;
    acc.chestRY += twistClamped * 0.42;
    acc.neckRY += twistClamped * 0.28 + this.lookOffset * 0.4;
    acc.hipsRY += twistClamped * 0.14;

    // Pitch: split between chest and neck so the weapon tracks the target.
    const pitch = this.aimPitch * this.aimBlend;
    acc.chestRX += -pitch * 0.45;
    acc.neckRX += -pitch * 0.45;
    acc.spineRX += -pitch * 0.15;

    // Mantle: tuck the legs while airborne.
    if (this.mantle > 0) {
      const k = clamp(this.mantle / 0.75, 0, 1);
      acc.hipLX += k * 1.1; acc.hipRX += k * 0.8;
      acc.knLX += k * 1.5; acc.knRX += k * 1.2;
      acc.spineRX += k * 0.25;
    }

    this._applyPose(acc, dt);
    this._plantFeet(speed, dt);
    this._solveWeapon(dt);

    // Hit flash on the cloth only, per-enemy material instance.
    const em = p.mats.cloth.emissive;
    if (em) em.setRGB(this.hitFlash * 0.30, 0, 0);
    void b;
  }

  /**
   * Ground pass for the legs. Two jobs, both of which the pose clips alone
   * cannot do: a boot must never sink through the floor, and while the soldier
   * is standing still both boots must actually be *on* the floor rather than
   * wherever the blended clip happened to leave them. Solved with the same
   * analytic two-bone IK the arms use, then the ankle is rolled flat.
   */
  _plantFeet(speed, dt) {
    if (this.mantle > 0 || !this.grounded) return;
    const b = this.parts.bones;
    const s = this.loadout.heightScale;
    const L1 = 0.44 * s, L2 = 0.42 * s;
    const SOLE = 0.076 * s;              // ankle height with the sole flat down
    // A leg is allowed to extend this far before the pelvis has to come down
    // instead. Past ~97% of full reach the knee visibly locks and the ankle
    // stops tracking its target, which is what the drop exists to prevent.
    const comfort = (L1 + L2) * 0.97;

    // Full plant when stationary, a partial one while moving: during a stride
    // the swing foot must stay where the clip put it, but the stance foot and
    // any ground penetration still have to be corrected.
    const plant = lerp(0.30, 1, clamp(1 - speed / 1.4, 0, 1));

    this.root.updateMatrixWorld(true);
    _qRig.setFromRotationMatrix(this.parts.rig.matrixWorld);
    _footPole.set(0, 0, 1).applyQuaternion(_qRig);   // knees break forward

    // --- 1. Where is the floor under each boot? A single plane under the root
    // puts a foot in the air the moment the soldier stands on a kerb or a
    // slope, so probe per foot. Throttled: two rays a frame per enemy is more
    // than this is worth, and feet do not move far in 60 ms.
    this._footProbe = (this._footProbe ?? 0) - (dt ?? 0);
    const reprobe = this._footProbe <= 0;
    if (reprobe) this._footProbe = 0.06 + this.id * 0.0037 % 0.03;
    if (!this._footGround) this._footGround = [0, 0];
    if (!this._footNormal) this._footNormal = [new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 1, 0)];

    for (let i = 0; i < 2; i++) {
      const an = i ? b.anR : b.anL;
      an.updateWorldMatrix(true, false);
      _footT.setFromMatrixPosition(an.matrixWorld);
      if (reprobe) {
        _v.copy(_footT); _v.y += 0.55;
        const hit = this.world.physics.raycast(_v, DOWN, 1.6);
        // Ignore anything that would put the foot above the hip or below the
        // capsule: that is a wall or a hole, not the floor this body is on.
        const gy = hit && Math.abs(hit.point.y - this.root.position.y) < 0.55
          ? hit.point.y : this.root.position.y;
        this._footGround[i] = gy;
        if (hit) this._footNormal[i].copy(hit.normal); else this._footNormal[i].set(0, 1, 0);
      }
    }

    // --- 2. Pelvis drop. The clips flex the knees, which lifts both boots off
    // the floor. Pulling them back down with IK is what straightens the legs
    // and makes the stance read stilted, so instead lower the pelvis by the
    // hover of the *nearer* boot: that one lands with its authored knee bend
    // intact, and only the other needs any IK at all.
    let hover = Infinity;
    for (let i = 0; i < 2; i++) {
      const an = i ? b.anR : b.anL;
      an.updateWorldMatrix(true, false);
      hover = Math.min(hover, _footT.setFromMatrixPosition(an.matrixWorld).y - (this._footGround[i] + SOLE));
    }
    let drop = Math.max(0, hover);
    // Second guard: a boot the pelvis drop has not brought within reach would
    // have to be met by a locked-straight leg. Take that out of the pelvis too.
    for (let i = 0; i < 2; i++) {
      const hip = i ? b.hipR : b.hipL;
      const an = i ? b.anR : b.anL;
      hip.updateWorldMatrix(true, false);
      an.updateWorldMatrix(true, false);
      _v.setFromMatrixPosition(hip.matrixWorld);
      _footT.setFromMatrixPosition(an.matrixWorld);
      _footT.y = this._footGround[i] + SOLE;
      // Lowering the hips shortens the reach to a target below them ~1:1.
      const need = _v.distanceTo(_footT) - drop;
      if (need > comfort) drop += need - comfort;
    }
    drop = clamp(drop * plant, 0, 0.18 * s);
    // Ease it in so a kerb doesn't pop the whole body down in one frame.
    this.pelvisDrop = dt ? damp(this.pelvisDrop ?? 0, drop, 14, dt) : drop;
    if (this.pelvisDrop > 1e-4) {
      b.hips.position.y -= this.pelvisDrop / s;
      b.hips.updateMatrixWorld(true);
    }

    // --- 3. Foot IK. Penetration is a hard constraint at any speed; pulling a
    // hovering foot down is weighted by `plant` so strides keep their lift.
    for (let i = 0; i < 2; i++) {
      const hip = i ? b.hipR : b.hipL;
      const kn = i ? b.knR : b.knL;
      const an = i ? b.anR : b.anL;
      an.updateWorldMatrix(true, false);
      _footT.setFromMatrixPosition(an.matrixWorld);
      const rest = this._footGround[i] + SOLE;
      const y = _footT.y < rest ? rest : lerp(_footT.y, rest, plant);
      if (Math.abs(y - _footT.y) > 5e-4) {
        _footT.y = y;
        ikArm(hip, kn, null, _footT, _footPole, L1, L2, 0);
      }
      // Roll the boot onto the surface it is standing on. The ankle's world
      // orientation becomes the rig's, tilted onto the ground normal.
      const n = this._footNormal[i];
      if (n.y > 0.7 && n.y < 0.999) {
        _q2.setFromUnitVectors(_up, n);
        _qFoot.copy(_q2).multiply(_qRig);
      } else _qFoot.copy(_qRig);
      kn.updateWorldMatrix(false, false);
      _q.setFromRotationMatrix(kn.matrixWorld).invert().multiply(_qFoot);
      an.quaternion.slerp(_q, plant);
      an.updateWorldMatrix(false, false);
    }
  }

  /** Place the weapon mount, then weld both hands to it with two-bone IK. */
  _solveWeapon(dt) {
    const p = this.parts;
    const b = p.bones;
    const W = WEAPON_POSES;
    this.root.updateMatrixWorld(true);

    // Blend the carry poses. Order matters: sprint and reload win outright.
    const ads = this.adsBlend;
    _mp.set(
      lerp(W.ready.p[0], W.ads.p[0], ads),
      lerp(W.ready.p[1], W.ads.p[1], ads),
      lerp(W.ready.p[2], W.ads.p[2], ads)
    );
    let rx = lerp(W.ready.r[0], W.ads.r[0], ads);
    let ry = lerp(W.ready.r[1], W.ads.r[1], ads);
    let rz = lerp(W.ready.r[2], W.ads.r[2], ads);
    const unaware = 1 - this.aimBlend;
    if (unaware > 1e-3) {
      _mp.lerp(_v.fromArray(W.patrol.p), unaware);
      rx = lerp(rx, W.patrol.r[0], unaware);
      ry = lerp(ry, W.patrol.r[1], unaware);
      rz = lerp(rz, W.patrol.r[2], unaware);
    }
    if (this.sprintBlend > 1e-3) {
      _mp.lerp(_v.fromArray(W.sprint.p), this.sprintBlend);
      rx = lerp(rx, W.sprint.r[0], this.sprintBlend);
      ry = lerp(ry, W.sprint.r[1], this.sprintBlend);
      rz = lerp(rz, W.sprint.r[2], this.sprintBlend);
    }
    if (this.reloadBlend > 1e-3) {
      _mp.lerp(_v.fromArray(W.reload.p), this.reloadBlend);
      rx = lerp(rx, W.reload.r[0], this.reloadBlend);
      ry = lerp(ry, W.reload.r[1], this.reloadBlend);
      rz = lerp(rz, W.reload.r[2], this.reloadBlend);
    }
    // Recoil kicks the gun back and up before the body follows it.
    rx -= this.recoil * 0.16;
    _mp.z += this.recoil * 0.028;

    p.mount.position.copy(_mp);
    _mr.set(rx, ry, rz);
    p.mount.quaternion.setFromEuler(_mr);
    p.mount.updateWorldMatrix(false, true);

    // Hand targets.
    _tgtA.setFromMatrixPosition(p.gripTrigger.matrixWorld);
    _tgtB.setFromMatrixPosition(p.gripSupport.matrixWorld);
    if (this.reloadBlend > 0.15) {
      // Support hand leaves the handguard for the magazine and comes back.
      const t = clamp(1 - this.reloading / 2.1, 0, 1);
      const k = this.reloadBlend * Math.sin(clamp(t * 2.6, 0, 1) * Math.PI);
      _v3.setFromMatrixPosition(p.magWell.matrixWorld);
      _tgtB.lerp(_v3, k);
      // Reach down to the pouch at the midpoint of the swap.
      _tgtB.y -= k * 0.24;
    }

    // Pole vectors: elbows sit down-and-out in the chest's frame. The model's
    // right (trigger) side is -X.
    b.chest.updateWorldMatrix(false, false);
    _q2.setFromRotationMatrix(b.chest.matrixWorld);
    // The firing elbow rides out and up, the support elbow tucks down and
    // slightly forward under the handguard. Both used to point almost straight
    // down, which buried the elbows inside the plate carrier and made the arms
    // read as one lump crossing the chest.
    _poleA.set(-1.15, -0.55, -0.30).applyQuaternion(_q2).normalize();
    _poleB.set(0.62, -1.00, 0.22).applyQuaternion(_q2).normalize();

    const L1 = this.armUpper, L2 = this.armLower;
    ikArm(b.shR, b.elR, b.wrR, _tgtA, _poleA, L1, L2, 0);
    ikArm(b.shL, b.elL, b.wrL, _tgtB, _poleB, L1, L2, 0);

    // Hands: roll the wrists so the palms wrap the grips.
    b.wrR.rotation.set(0.30, 0, 0.35);
    b.wrL.rotation.set(0.25, 0, -0.30);
    void dt;
  }

  /**
   * Pose channels are authored as "positive = forward / flexed", which maps to
   * different signs per chain: the spine's bone axis points up while the limb
   * bones point down, so a forward torso lean and a forward leg swing have
   * opposite rotation signs. Knee flexion always swings the foot rearward.
   */
  _applyPose(o, dt) {
    const b = this.parts.bones;
    b.hips.position.set(0, 0.98 + o.hipsY - this.crouchBlend * 0.30, o.hipsZoff);
    b.hips.rotation.set(o.hipsRX, o.hipsRY, o.hipsRZ);
    b.spine.rotation.set(o.spineRX, o.spineRY, o.spineRZ);
    b.chest.rotation.set(o.chestRX, o.chestRY, o.chestRZ);
    b.neck.rotation.set(o.neckRX, o.neckRY, o.neckRZ);
    b.head.rotation.set(0, 0, 0);
    b.clavL.rotation.set(0, 0, -o.clavLZ);
    b.clavR.rotation.set(0, 0, o.clavRZ);
    b.hipL.rotation.set(-o.hipLX, o.hipLY, -o.hipLZ);
    b.knL.rotation.set(Math.max(0, o.knLX), 0, 0);
    b.anL.rotation.set(o.anLX, 0, -o.anLZ);
    b.hipR.rotation.set(-o.hipRX, o.hipRY, -o.hipRZ);
    b.knR.rotation.set(Math.max(0, o.knRX), 0, 0);
    b.anR.rotation.set(o.anRX, 0, -o.anRZ);
    // Arms are driven entirely by the weapon IK in _solveWeapon.
    void dt;
  }

  /* ------------------------------------------------------------- death --- */

  _updateDeath(dt) {
    this.deathTimer += dt;
    if (this.ragdoll) {
      // Fixed sub-steps keep the Verlet solve stable at any frame rate.
      let rest = Math.min(dt, 0.1);
      while (rest > 0) {
        const h = Math.min(rest, 1 / 90);
        this.ragdoll.step(h);
        rest -= h;
      }
      this.ragdoll.apply(this.parts, this.yaw, this.loadout.heightScale);
      // Keep the physics capsule roughly on the corpse so nothing else trips.
      this.capsule.start.copy(this.ragdoll.p[RDI.hips]);
      this.capsule.end.copy(this.ragdoll.p[RDI.chest]);
    }
    const em = this.parts.mats.cloth.emissive;
    if (em) em.setRGB(this.hitFlash * 0.30, 0, 0);

    if (this.deathTimer > 14 && !this._removed) this.despawn();
  }

  /**
   * Take this bot out of the world immediately.
   *
   * Setting `_removed` alone is not enough and was leaving bots standing in the
   * map: the flag is only ever acted on here, inside `update()`, and a round
   * restart empties `game.enemies` in the same breath — so `update()` never ran
   * again, the mesh was never removed from the scene, and the squad blackboard
   * kept a dead reference. Anything that drops a bot outside the normal death
   * timeout has to call this.
   */
  despawn() {
    if (this._removed) return;
    this._removed = true;
    this.alive = false;
    this.world.scene.remove(this.root);
    squadLeave(this);
  }
}

Enemy._tickedThisFrame = false;

/* Debug/authoring hooks used by tools/shot-enemy.mjs. */
Enemy.SQUAD = SQUAD;
/** Report a shot so the opposing team can react. Wired from Game. */
Enemy.hearShot = squadHearShot;
